import React, { useRef, useEffect, useState, useCallback, useMemo, useLayoutEffect } from 'react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, Grid, Environment, Html, Text, useGLTF } from '@react-three/drei';
import * as THREE from 'three';

// ---------------------------------------------------------------
// Sub-component: CameraAnimator for smooth preset transitions (Top, Side, Front, Isometric)
// ---------------------------------------------------------------
// Sub-component: CameraAnimator for smooth preset transitions (Top, Side, Front, Isometric)
// ---------------------------------------------------------------
const CameraAnimator = ({ cameraPreset, orbitControlsRef, sceneBounds }) => {
  const { camera } = useThree();
  const animRef = useRef(null);

  useEffect(() => {
    if (!cameraPreset) return;

    const { centerX, centerZ, maxDim } = sceneBounds || { centerX: 0, centerZ: 0, maxDim: 20 };
    const d = Math.max(maxDim, 15);
    const targetLook = new THREE.Vector3(centerX, 0, centerZ);
    const targetPos = new THREE.Vector3();
    const targetUp = new THREE.Vector3(0, 1, 0);
    let targetFov = 50;

    if (cameraPreset.type === 'top') {
      // True Orthographic-Flat Top View (Plan View)
      // Small FOV (2.2 degrees) at high altitude gives true zero-perspective-distortion orthogonal projection
      // Vertical pillars and masts have exactly 0° tilt, so the top aligns 100% with the base
      targetFov = 2.2;
      const halfFovRad = (targetFov / 2) * (Math.PI / 180);
      const fitDim = Math.max(maxDim * 1.35, 18);
      const camHeight = (fitDim / 2) / Math.tan(halfFovRad);

      // Dead center directly overhead looking straight down
      targetPos.set(centerX, camHeight, centerZ);
      targetLook.set(centerX, 0, centerZ);
      // In Top View, looking down (-Y), North (-Z) is the top of the screen
      targetUp.set(0, 0, -1);
    } else if (cameraPreset.type === 'side') {
      // Side view from the right (+X)
      targetPos.set(centerX + d * 1.15, Math.max(3, d * 0.22), centerZ);
      targetLook.set(centerX, Math.max(1, d * 0.08), centerZ);
      targetFov = 50;
      targetUp.set(0, 1, 0);
    } else if (cameraPreset.type === 'front') {
      // Front view from the front (+Z)
      targetPos.set(centerX, Math.max(3, d * 0.22), centerZ + d * 1.15);
      targetLook.set(centerX, Math.max(1, d * 0.08), centerZ);
      targetFov = 50;
      targetUp.set(0, 1, 0);
    } else {
      // Isometric view (classic 45-degree angle axonometric perspective)
      targetPos.set(centerX + d * 0.85, d * 0.8, centerZ + d * 0.85);
      targetLook.set(centerX, 0, centerZ);
      targetFov = 50;
      targetUp.set(0, 1, 0);
    }

    const startPos = camera.position.clone();
    const startLook = orbitControlsRef.current?.target
      ? orbitControlsRef.current.target.clone()
      : new THREE.Vector3(centerX, 0, centerZ);
    const startFov = camera.fov || 50;
    const startUp = camera.up.clone();

    animRef.current = {
      startPos,
      targetPos,
      startLook,
      targetLook,
      startFov,
      targetFov,
      startUp,
      targetUp,
      startTime: performance.now(),
      duration: 550
    };
  }, [cameraPreset, sceneBounds, camera, orbitControlsRef]);

  useFrame(() => {
    if (!animRef.current) return;
    const { startPos, targetPos, startLook, targetLook, startFov, targetFov, startUp, targetUp, startTime, duration } = animRef.current;
    const elapsed = performance.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);

    // EaseInOutCubic easing for smooth transition
    const ease = progress < 0.5
      ? 4 * progress * progress * progress
      : 1 - Math.pow(-2 * progress + 2, 3) / 2;

    camera.position.lerpVectors(startPos, targetPos, ease);
    camera.up.lerpVectors(startUp, targetUp, ease).normalize();

    if (Math.abs(startFov - targetFov) > 0.01) {
      camera.fov = THREE.MathUtils.lerp(startFov, targetFov, ease);
      camera.updateProjectionMatrix();
    }

    if (orbitControlsRef.current) {
      orbitControlsRef.current.target.lerpVectors(startLook, targetLook, ease);
      orbitControlsRef.current.update();
    } else {
      camera.lookAt(targetLook);
    }

    if (progress >= 1) {
      camera.position.copy(targetPos);
      camera.up.copy(targetUp);
      camera.fov = targetFov;
      camera.updateProjectionMatrix();
      if (orbitControlsRef.current) {
        orbitControlsRef.current.target.copy(targetLook);
        orbitControlsRef.current.update();
      }
      animRef.current = null;
    }
  });

  return null;
};

// ---------------------------------------------------------------
// Sub-component: syncs camera to overlay transform
// ---------------------------------------------------------------

// --- InstancedBoxes ---
const InstancedBoxes = ({ count, positions, args, color }) => {
  const meshRef = useRef();
  const dummy = useMemo(() => new THREE.Object3D(), []);

  useLayoutEffect(() => {
    if (meshRef.current && positions.length > 0) {
      for (let i = 0; i < count; i++) {
        dummy.position.set(positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2]);
        dummy.updateMatrix();
        meshRef.current.setMatrixAt(i, dummy.matrix);
      }
      meshRef.current.instanceMatrix.needsUpdate = true;
    }
  }, [count, positions]);

  if (count === 0) return null;
  return (
    <instancedMesh ref={meshRef} args={[null, null, count]}>
      <boxGeometry args={args} />
      <meshStandardMaterial color={color} roughness={0.8} />
    </instancedMesh>
  );
};

// --- InstancedPallets: Realistic Pallet Loads (Base + Cardboard Cargo + Top Strap) ---
const InstancedPallets = ({ count, positions, args, baseColor = '#d97706' }) => {
  const baseMeshRef = useRef();
  const cargoMeshRef = useRef();
  const strapMeshRef = useRef();
  const dummy = useMemo(() => new THREE.Object3D(), []);

  const [dimX, totalHeight, dimZ] = args || [1.0, 1.5, 1.2];
  const baseHeightM = 0.14;
  const cargoHeightM = Math.max(0.1, totalHeight - baseHeightM);

  const baseArgs = useMemo(() => [dimX * 0.98, baseHeightM, dimZ * 0.98], [dimX, dimZ]);
  const cargoArgs = useMemo(() => [dimX * 0.93, cargoHeightM, dimZ * 0.93], [dimX, cargoHeightM, dimZ]);
  const strapArgs = useMemo(() => [dimX * 0.935, 0.02, dimZ * 0.935], [dimX, dimZ]);

  useLayoutEffect(() => {
    if (!positions || count <= 0) return;
    for (let i = 0; i < count; i++) {
      const px = positions[i * 3];
      const centerY = positions[i * 3 + 1];
      const pz = positions[i * 3 + 2];
      const bottomY = centerY - totalHeight / 2;

      // 1. Wooden / Loscam / Chep Pallet Base
      dummy.position.set(px, bottomY + baseHeightM / 2, pz);
      dummy.updateMatrix();
      if (baseMeshRef.current) baseMeshRef.current.setMatrixAt(i, dummy.matrix);

      // 2. Realistic Cargo Load Box (#fef3c7) matching conveyor
      dummy.position.set(px, bottomY + baseHeightM + cargoHeightM / 2, pz);
      dummy.updateMatrix();
      if (cargoMeshRef.current) cargoMeshRef.current.setMatrixAt(i, dummy.matrix);

      // 3. Top Cargo Corner Edge / Strapping Accent (#f59e0b)
      dummy.position.set(px, bottomY + baseHeightM + cargoHeightM - 0.01, pz);
      dummy.updateMatrix();
      if (strapMeshRef.current) strapMeshRef.current.setMatrixAt(i, dummy.matrix);
    }
    if (baseMeshRef.current) baseMeshRef.current.instanceMatrix.needsUpdate = true;
    if (cargoMeshRef.current) cargoMeshRef.current.instanceMatrix.needsUpdate = true;
    if (strapMeshRef.current) strapMeshRef.current.instanceMatrix.needsUpdate = true;
  }, [count, positions, totalHeight, baseHeightM, cargoHeightM, dummy]);

  if (count === 0) return null;

  return (
    <group>
      {/* 1. Pallet Base */}
      <instancedMesh ref={baseMeshRef} args={[null, null, count]} castShadow receiveShadow>
        <boxGeometry args={baseArgs} />
        <meshStandardMaterial color={baseColor} roughness={0.75} />
      </instancedMesh>

      {/* 2. Realistic Cargo Box */}
      <instancedMesh ref={cargoMeshRef} args={[null, null, count]} castShadow receiveShadow>
        <boxGeometry args={cargoArgs} />
        <meshStandardMaterial color="#fef3c7" roughness={0.4} />
      </instancedMesh>

      {/* 3. Top Strapping Accent */}
      <instancedMesh ref={strapMeshRef} args={[null, null, count]} castShadow receiveShadow>
        <boxGeometry args={strapArgs} />
        <meshStandardMaterial color="#f59e0b" roughness={0.5} />
      </instancedMesh>
    </group>
  );
};

const CameraSync = ({ onCameraUpdate }) => {
  const { camera, gl } = useThree();
  
  useEffect(() => {
    const handleRender = () => {
      onCameraUpdate(camera);
    };
    
    gl.setAnimationLoop(handleRender);
    return () => gl.setAnimationLoop(null);
  }, [camera, gl, onCameraUpdate]);
  
  return null;
};

// ---------------------------------------------------------------
// Sub-component: loads texture safely
// ---------------------------------------------------------------
const TexturePlane = ({ src, bgW, bgH, pxToM, rotation = 0 }) => {
  const [tex, setTex] = useState(null);
  const rw = bgW * pxToM;
  const rh = bgH * pxToM;

  useEffect(() => {
    if (!src) return;

    const img = new Image();
    if (src && !src.startsWith('blob:') && !src.startsWith('data:')) {
      img.crossOrigin = "Anonymous";
    }
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const maxDim = 2048;
      const renderScale = Math.min(maxDim / bgW, maxDim / bgH);
      canvas.width = bgW * renderScale;
      canvas.height = bgH * renderScale;
      const ctx = canvas.getContext('2d');
      
      // Fix aspect ratio distortion by letterboxing the image
      const imgScale = Math.min(canvas.width / img.width, canvas.height / img.height);
      const drawW = img.width * imgScale;
      const drawH = img.height * imgScale;
      const drawX = (canvas.width - drawW) / 2;
      const drawY = (canvas.height - drawH) / 2;
      
      if (rotation !== 0) {
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(rotation * Math.PI / 180);
        ctx.translate(-canvas.width / 2, -canvas.height / 2);
      }
      
      ctx.drawImage(img, drawX, drawY, drawW, drawH);
      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.anisotropy = 4;
      setTex(texture);
    };

    img.onerror = (e) => console.error("Failed to load map texture for 3D", e);
    img.src = src;
  }, [src, bgW, bgH, rotation]);

  if (!tex) return null;

  return (
    <mesh position={[0, -0.01, 0]} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[rw, rh]} />
      <meshBasicMaterial map={tex} transparent opacity={0.8} />
    </mesh>
  );
};

// ---------------------------------------------------------------
// 3D Human Worker Model (Realistic 1.70m Ergonomic Industrial Operator)
// ---------------------------------------------------------------
const HumanWorker3D = ({ role = 'picking', showHeightBadge = false }) => {
  // Total Height: Exactly 1.70m (170cm) from floor to top of safety hard hat
  const skinColor = '#fed7aa'; // Warm natural skin tone
  const pantsColor = '#1e293b'; // Navy industrial utility trousers
  const vestColor = '#ea580c'; // High-vis Toyota safety orange
  const helmetColor = '#eab308'; // High-gloss safety yellow
  const helmetBrim = '#ca8a04';
  const bootColor = '#0f172a'; // Steel-toe work boots
  const gloveColor = '#f8fafc'; // White/grey grip gloves

  const roleLabel = role === 'picking' ? 'Picking' : role === 'packing' ? 'Packing' : role === 'qc' ? 'QC' : role === 'loading' ? 'Loading' : 'Operator';

  return (
    <group>
      {/* 1. Anti-Fatigue Safety Rubber Mat on floor */}
      <mesh position={[0, 0.007, 0]}>
        <boxGeometry args={[0.72, 0.014, 0.72]} />
        <meshStandardMaterial color="#334155" roughness={0.9} />
      </mesh>
      {/* Mat Yellow Safety Chamfer Border */}
      <mesh position={[0, 0.004, 0]}>
        <boxGeometry args={[0.76, 0.008, 0.76]} />
        <meshStandardMaterial color="#f59e0b" roughness={0.6} />
      </mesh>

      {/* 2. Steel-Toe Safety Boots (H: 0 - 0.12m) */}
      {[-0.10, 0.10].map((bx, i) => (
        <group key={`boot-${i}`} position={[bx, 0, 0]}>
          {/* Outsole Grip */}
          <mesh position={[0, 0.018, 0.02]}>
            <boxGeometry args={[0.11, 0.035, 0.24]} />
            <meshStandardMaterial color="#020617" roughness={0.95} />
          </mesh>
          {/* Leather Upper */}
          <mesh position={[0, 0.065, 0.01]}>
            <boxGeometry args={[0.10, 0.07, 0.22]} />
            <meshStandardMaterial color={bootColor} roughness={0.7} />
          </mesh>
          {/* Steel Toe Cap Front Accent */}
          <mesh position={[0, 0.045, 0.09]}>
            <boxGeometry args={[0.095, 0.05, 0.05]} />
            <meshStandardMaterial color="#020617" roughness={0.5} />
          </mesh>
        </group>
      ))}

      {/* 3. Legs & Utility Trousers (H: 0.12m - 0.82m) */}
      {[-0.10, 0.10].map((lx, i) => {
        const angle = i === 0 ? 0.025 : -0.025; // Subtle natural standing stance
        return (
          <group key={`leg-${i}`} position={[lx, 0, 0]} rotation={[0, 0, angle]}>
            {/* Lower Leg / Calf (0.12m to 0.48m) */}
            <mesh position={[0, 0.30, 0]}>
              <cylinderGeometry args={[0.062, 0.052, 0.36, 12]} />
              <meshStandardMaterial color={pantsColor} roughness={0.8} />
            </mesh>
            {/* Knee Pad Protector */}
            <mesh position={[0, 0.48, 0.045]}>
              <boxGeometry args={[0.09, 0.08, 0.03]} />
              <meshStandardMaterial color="#334155" roughness={0.6} />
            </mesh>
            {/* Upper Leg / Thigh (0.48m to 0.82m) */}
            <mesh position={[0, 0.65, 0]}>
              <cylinderGeometry args={[0.075, 0.062, 0.34, 12]} />
              <meshStandardMaterial color={pantsColor} roughness={0.8} />
            </mesh>
          </group>
        );
      })}

      {/* 4. Pelvis & Utility Belt (H: 0.82m - 0.88m) */}
      <mesh position={[0, 0.84, 0]}>
        <boxGeometry args={[0.31, 0.08, 0.20]} />
        <meshStandardMaterial color={pantsColor} roughness={0.8} />
      </mesh>
      {/* Heavy-duty Tool Belt */}
      <mesh position={[0, 0.87, 0]}>
        <boxGeometry args={[0.325, 0.045, 0.215]} />
        <meshStandardMaterial color="#0f172a" roughness={0.7} />
      </mesh>
      {/* Belt Buckle */}
      <mesh position={[0, 0.87, 0.11]}>
        <boxGeometry args={[0.05, 0.035, 0.01]} />
        <meshStandardMaterial color="#94a3b8" metalness={0.9} roughness={0.2} />
      </mesh>
      {/* Radio Holster / Pouch */}
      <mesh position={[0.17, 0.84, 0.02]}>
        <boxGeometry args={[0.04, 0.09, 0.05]} />
        <meshStandardMaterial color="#0f172a" roughness={0.8} />
      </mesh>

      {/* 5. Torso & High-Vis Safety Vest (H: 0.88m - 1.38m) */}
      {/* Inner Shirt Collar (Toyota Blue) */}
      <mesh position={[0, 1.36, 0]}>
        <cylinderGeometry args={[0.09, 0.11, 0.05, 12]} />
        <meshStandardMaterial color="#0284c7" roughness={0.7} />
      </mesh>
      {/* Main Torso Vest (Tapered human upper body) */}
      <mesh position={[0, 1.13, 0]}>
        <cylinderGeometry args={[0.18, 0.155, 0.48, 16]} scale={[1.15, 1, 0.7]} />
        <meshStandardMaterial color={vestColor} roughness={0.45} />
      </mesh>

      {/* Reflective Stripes (ISO 20471 High-Vis Standard) */}
      {/* Lower Horizontal Reflective Band */}
      <mesh position={[0, 0.98, 0]}>
        <cylinderGeometry args={[0.162, 0.158, 0.045, 16]} scale={[1.16, 1, 0.71]} />
        <meshStandardMaterial color="#ffffff" roughness={0.15} metalness={0.4} />
      </mesh>
      {/* Upper Horizontal Chest Reflective Band */}
      <mesh position={[0, 1.20, 0]}>
        <cylinderGeometry args={[0.182, 0.178, 0.045, 16]} scale={[1.16, 1, 0.71]} />
        <meshStandardMaterial color="#ffffff" roughness={0.15} metalness={0.4} />
      </mesh>
      {/* Left Vertical Reflective Suspender Band */}
      <mesh position={[-0.09, 1.27, 0.07]}>
        <boxGeometry args={[0.038, 0.20, 0.01]} />
        <meshStandardMaterial color="#ffffff" roughness={0.15} metalness={0.4} />
      </mesh>
      {/* Right Vertical Reflective Suspender Band */}
      <mesh position={[0.09, 1.27, 0.07]}>
        <boxGeometry args={[0.038, 0.20, 0.01]} />
        <meshStandardMaterial color="#ffffff" roughness={0.15} metalness={0.4} />
      </mesh>
      {/* ID Badge on Chest */}
      <mesh position={[-0.09, 1.22, 0.08]}>
        <boxGeometry args={[0.045, 0.06, 0.005]} />
        <meshStandardMaterial color="#f8fafc" roughness={0.2} />
      </mesh>
      <mesh position={[-0.09, 1.26, 0.082]}>
        <boxGeometry args={[0.015, 0.02, 0.002]} />
        <meshStandardMaterial color="#dc2626" />
      </mesh>

      {/* 6. Neck (H: 1.38m - 1.44m) */}
      <mesh position={[0, 1.41, 0]}>
        <cylinderGeometry args={[0.055, 0.06, 0.06, 12]} />
        <meshStandardMaterial color={skinColor} roughness={0.6} />
      </mesh>

      {/* 7. Head & Face (H: 1.44m - 1.58m) */}
      <mesh position={[0, 1.50, 0]} scale={[0.96, 1.05, 0.96]}>
        <sphereGeometry args={[0.092, 24, 24]} />
        <meshStandardMaterial color={skinColor} roughness={0.55} />
      </mesh>
      {/* Hair Under Helmet Rim */}
      <mesh position={[0, 1.52, -0.02]} scale={[0.98, 1.04, 0.98]}>
        <sphereGeometry args={[0.093, 16, 16, 0, Math.PI * 2, 0, Math.PI * 0.45]} />
        <meshStandardMaterial color="#292524" roughness={0.9} />
      </mesh>
      {/* Sleek Safety Glasses (Cyan/Blue Tint Wrap-around Visor) */}
      <mesh position={[0, 1.515, 0.082]}>
        <boxGeometry args={[0.13, 0.028, 0.035]} />
        <meshStandardMaterial color="#0284c7" roughness={0.1} metalness={0.1} transparent opacity={0.88} />
      </mesh>
      <mesh position={[0, 1.515, 0.075]}>
        <boxGeometry args={[0.135, 0.006, 0.04]} />
        <meshStandardMaterial color="#0f172a" roughness={0.4} />
      </mesh>

      {/* 8. Industrial Safety Hard Hat (Peak at EXACTLY 1.70m) */}
      {/* Main Dome: position Y=1.575 + (radius 0.125 = 1.70m top peak!) */}
      <mesh position={[0, 1.575, 0.005]}>
        <sphereGeometry args={[0.125, 24, 16, 0, Math.PI * 2, 0, Math.PI * 0.52]} />
        <meshStandardMaterial color={helmetColor} roughness={0.3} />
      </mesh>
      {/* Top Reinforcement Crown Rib */}
      <mesh position={[0, 1.692, 0.01]}>
        <boxGeometry args={[0.028, 0.016, 0.16]} />
        <meshStandardMaterial color={helmetBrim} roughness={0.4} />
      </mesh>
      {/* Helmet Brim / Sun Visor */}
      <mesh position={[0, 1.56, 0.075]} rotation={[-0.12, 0, 0]}>
        <boxGeometry args={[0.18, 0.015, 0.12]} />
        <meshStandardMaterial color={helmetBrim} roughness={0.4} />
      </mesh>
      {/* Helmet Chin Strap */}
      <mesh position={[0, 1.46, 0.02]}>
        <cylinderGeometry args={[0.094, 0.094, 0.008, 16, 1, true]} />
        <meshStandardMaterial color="#1e293b" />
      </mesh>
      {/* Toyota Red Helmet Decal Accent */}
      <mesh position={[0, 1.625, 0.122]}>
        <boxGeometry args={[0.03, 0.012, 0.005]} />
        <meshStandardMaterial color="#ef4444" roughness={0.3} />
      </mesh>

      {/* 9. Arms & Natural Active Ergonomic Posture */}
      {/* Left Arm: Holding Digital Scanner / PDA Terminal */}
      <mesh position={[-0.20, 1.33, 0]}>
        <sphereGeometry args={[0.055, 12, 12]} />
        <meshStandardMaterial color={vestColor} roughness={0.5} />
      </mesh>
      <mesh position={[-0.22, 1.20, 0.05]} rotation={[0.45, 0, 0.15]}>
        <cylinderGeometry args={[0.048, 0.042, 0.26, 12]} />
        <meshStandardMaterial color={vestColor} roughness={0.5} />
      </mesh>
      <mesh position={[-0.16, 1.07, 0.20]} rotation={[1.15, -0.35, 0]}>
        <cylinderGeometry args={[0.042, 0.038, 0.25, 12]} />
        <meshStandardMaterial color={skinColor} roughness={0.6} />
      </mesh>
      <mesh position={[-0.13, 1.04, 0.30]} rotation={[0, -0.3, 0]}>
        <sphereGeometry args={[0.038, 12, 12]} />
        <meshStandardMaterial color={gloveColor} roughness={0.7} />
      </mesh>
      {/* Digital Barcode Scanner / Handheld Terminal */}
      <mesh position={[-0.12, 1.06, 0.34]} rotation={[0.2, -0.3, 0]}>
        <boxGeometry args={[0.05, 0.03, 0.12]} />
        <meshStandardMaterial color="#0f172a" roughness={0.3} />
      </mesh>
      <mesh position={[-0.12, 1.08, 0.33]} rotation={[-Math.PI / 4, -0.3, 0]}>
        <planeGeometry args={[0.04, 0.05]} />
        <meshBasicMaterial color="#38bdf8" />
      </mesh>

      {/* Right Arm: Natural Resting / Guiding Posture */}
      <mesh position={[0.20, 1.33, 0]}>
        <sphereGeometry args={[0.055, 12, 12]} />
        <meshStandardMaterial color={vestColor} roughness={0.5} />
      </mesh>
      <mesh position={[0.22, 1.20, 0.05]} rotation={[0.40, 0, -0.15]}>
        <cylinderGeometry args={[0.048, 0.042, 0.26, 12]} />
        <meshStandardMaterial color={vestColor} roughness={0.5} />
      </mesh>
      <mesh position={[0.18, 1.08, 0.18]} rotation={[0.95, 0.25, 0]}>
        <cylinderGeometry args={[0.042, 0.038, 0.25, 12]} />
        <meshStandardMaterial color={skinColor} roughness={0.6} />
      </mesh>
      <mesh position={[0.15, 1.05, 0.29]}>
        <sphereGeometry args={[0.038, 12, 12]} />
        <meshStandardMaterial color={gloveColor} roughness={0.7} />
      </mesh>

      {/* 10. Floating Height & Role Badge when Show Dimensions is ON */}
      {showHeightBadge && (
        <Html position={[0, 1.83, 0]} center>
          <div style={{
            background: 'rgba(15, 23, 42, 0.92)',
            color: 'white',
            padding: '2px 7px',
            borderRadius: '6px',
            fontSize: '10px',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            userSelect: 'none',
            boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
            border: '1px solid rgba(234, 179, 8, 0.5)',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <span style={{ color: '#facc15' }}>●</span>
            <span>{roleLabel}</span>
            <span style={{ opacity: 0.5 }}>|</span>
            <span style={{ color: '#38bdf8' }}>H: 170 cm</span>
          </div>
        </Html>
      )}
    </group>
  );
};

// --- Custom 3D Model Instance (.glb loader with auto-scaling to target height) ---
const GLBModelInstance = ({ url, targetHeight = 1.70, role = 'picking', showHeightBadge = false }) => {
  const { scene } = useGLTF(url);
  const clonedScene = useMemo(() => {
    if (!scene) return null;
    const clone = scene.clone(true);
    // Measure original bounding box
    const box = new THREE.Box3().setFromObject(clone);
    const size = new THREE.Vector3();
    box.getSize(size);
    
    // Scale so that model height (Y axis) matches targetHeight (e.g. 1.70m)
    const currentHeight = size.y;
    const scale = (currentHeight > 0.001) ? (targetHeight / currentHeight) : 1;
    clone.scale.set(scale, scale, scale);
    
    // Recompute scaled box to center model horizontally at (0, 0)
    const scaledBox = new THREE.Box3().setFromObject(clone);
    const scaledCenter = new THREE.Vector3();
    scaledBox.getCenter(scaledCenter);

    // Center X & Z on origin and ground bottom at Y = 0
    clone.position.x = -scaledCenter.x;
    clone.position.z = -scaledCenter.z;
    clone.position.y = -scaledBox.min.y;

    clone.traverse(child => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
    return clone;
  }, [scene, targetHeight]);

  const roleLabel = role === 'picking' ? 'Picking' : role === 'packing' ? 'Packing' : role === 'qc' ? 'QC' : role === 'loading' ? 'Loading' : 'Operator';

  return (
    <group>
      {/* Anti-Fatigue Safety Rubber Mat on floor (operators only) */}
      {(role === 'operator' || role === 'picking' || role === 'packing' || role === 'qc') && (
        <>
          <mesh position={[0, 0.007, 0]}>
            <boxGeometry args={[0.72, 0.014, 0.72]} />
            <meshStandardMaterial color="#334155" roughness={0.9} />
          </mesh>
          <mesh position={[0, 0.004, 0]}>
            <boxGeometry args={[0.76, 0.008, 0.76]} />
            <meshStandardMaterial color="#f59e0b" roughness={0.6} />
          </mesh>
        </>
      )}

      {clonedScene && <primitive object={clonedScene} />}

      {showHeightBadge && (
        <Html position={[0, targetHeight + 0.15, 0]} center>
          <div style={{
            background: 'rgba(15, 23, 42, 0.94)',
            color: 'white',
            padding: '2px 7px',
            borderRadius: '6px',
            fontSize: '10px',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            userSelect: 'none',
            boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
            border: '1px solid rgba(56, 189, 248, 0.6)',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <span style={{ color: '#38bdf8' }}>★</span>
            <span>{roleLabel}</span>
            <span style={{ opacity: 0.5 }}>|</span>
            <span style={{ color: '#34d399' }}>Custom 3D: {Math.round(targetHeight * 100)} cm</span>
          </div>
        </Html>
      )}
    </group>
  );
};

// Safe Error Boundary for dynamically loaded 3D models
class ModelErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(err) {
    console.warn("Could not render custom 3D model, falling back to procedural model:", err);
  }
  render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

// Wrapper that selects between Active Custom 3D Model and Built-in Model
const OperatorMesh = ({ role, showHeightBadge, customModel }) => {
  if (customModel && customModel.file_url) {
    return (
      <ModelErrorBoundary fallback={<HumanWorker3D role={role} showHeightBadge={showHeightBadge} />}>
        <React.Suspense fallback={<HumanWorker3D role={role} showHeightBadge={showHeightBadge} />}>
          <GLBModelInstance 
            url={customModel.file_url} 
            targetHeight={customModel.target_height_m || 1.70} 
            role={role} 
            showHeightBadge={showHeightBadge} 
          />
        </React.Suspense>
      </ModelErrorBoundary>
    );
  }
  return <HumanWorker3D role={role} showHeightBadge={showHeightBadge} />;
};

// ---------------------------------------------------------------
// 3D Procedural Empty Wooden Pallet with Forklift Pockets
// ---------------------------------------------------------------
const EmptyPalletMesh3D = ({ width, length, height = 0.14, color = '#d97706', yOffset = 0 }) => {
  const topThick = 0.024;
  const botThick = 0.022;
  const runnerH = Math.max(0.04, height - topThick - botThick);
  const runnerW = Math.max(0.04, width * 0.08);

  return (
    <group position={[0, yOffset, 0]}>
      {/* Top Deck Surface / Slats */}
      <mesh position={[0, height - topThick / 2, 0]}>
        <boxGeometry args={[width * 0.98, topThick, length * 0.98]} />
        <meshStandardMaterial color={color} roughness={0.75} />
      </mesh>
      {/* Bottom Deck Chamfer Boards */}
      <mesh position={[0, botThick / 2, 0]}>
        <boxGeometry args={[width * 0.96, botThick, length * 0.96]} />
        <meshStandardMaterial color={color} roughness={0.8} />
      </mesh>
      {/* 3 Stringers / Runners with Forklift Pockets */}
      {[-1, 0, 1].map(side => (
        <mesh key={side} position={[side * (width * 0.42), botThick + runnerH / 2, 0]}>
          <boxGeometry args={[runnerW, runnerH, length * 0.96]} />
          <meshStandardMaterial color={color} roughness={0.85} />
        </mesh>
      ))}
    </group>
  );
};

// ---------------------------------------------------------------
// 3D Procedural Pallet with Cargo or Empty Pallet Stack
// ---------------------------------------------------------------
const PalletWithCargo3D = ({ 
  width, 
  length, 
  baseHeight = 0.14, 
  cargoHeight = 1.2, 
  baseColor = '#d97706', 
  cargoColor = '#fef3c7',
  yOffset = 0,
  isEmpty = false,
  emptyStack = 1
}) => {
  if (isEmpty) {
    return (
      <group position={[0, yOffset, 0]}>
        {Array.from({ length: Math.max(1, emptyStack) }).map((_, s) => (
          <EmptyPalletMesh3D
            key={s}
            width={width}
            length={length}
            height={baseHeight}
            color={baseColor}
            yOffset={s * (baseHeight + 0.003)}
          />
        ))}
      </group>
    );
  }

  return (
    <group position={[0, yOffset, 0]}>
      <EmptyPalletMesh3D
        width={width}
        length={length}
        height={baseHeight}
        color={baseColor}
        yOffset={0}
      />
      {/* Realistic Cargo Load Box */}
      <mesh position={[0, baseHeight + cargoHeight / 2, 0]}>
        <boxGeometry args={[width * 0.93, cargoHeight, length * 0.93]} />
        <meshStandardMaterial color={cargoColor} roughness={0.4} />
      </mesh>
      {/* Top Cargo Corner Edge / Strapping Accent */}
      <mesh position={[0, baseHeight + cargoHeight - 0.01, 0]}>
        <boxGeometry args={[width * 0.935, 0.02, length * 0.935]} />
        <meshStandardMaterial color="#f59e0b" roughness={0.5} />
      </mesh>
    </group>
  );
};

// ---------------------------------------------------------------
// 3D Procedural Reach Truck (Toyota 8FBRE Industrial Quality)
// ---------------------------------------------------------------
const ProceduralReachTruck3D = ({ targetHeight = 2.30, showHeightBadge = false, modelName = 'Toyota Reach Truck 8FBRE' }) => {
  const truckScale = targetHeight / 2.30;
  return (
    <group scale={[truckScale, truckScale, truckScale]}>
      {/* 1. Outrigger Support Legs (Front Base) */}
      <mesh position={[-0.45, 0.12, 0.55]}>
        <boxGeometry args={[0.16, 0.24, 1.2]} />
        <meshStandardMaterial color="#1e293b" roughness={0.5} />
      </mesh>
      <mesh position={[0.45, 0.12, 0.55]}>
        <boxGeometry args={[0.16, 0.24, 1.2]} />
        <meshStandardMaterial color="#1e293b" roughness={0.5} />
      </mesh>
      {/* Front Load Wheels */}
      <mesh position={[-0.45, 0.07, 1.05]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.07, 0.07, 0.14, 16]} />
        <meshStandardMaterial color="#94a3b8" roughness={0.4} />
      </mesh>
      <mesh position={[0.45, 0.07, 1.05]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.07, 0.07, 0.14, 16]} />
        <meshStandardMaterial color="#94a3b8" roughness={0.4} />
      </mesh>

      {/* 2. Main Chassis & Battery Body */}
      <mesh position={[0, 0.55, -0.35]}>
        <boxGeometry args={[1.05, 0.95, 1.0]} />
        <meshStandardMaterial color="#0284c7" roughness={0.3} metalness={0.2} />
      </mesh>
      {/* Lower Counterweight / Steel Sills */}
      <mesh position={[0, 0.12, -0.35]}>
        <boxGeometry args={[1.08, 0.22, 1.04]} />
        <meshStandardMaterial color="#0f172a" roughness={0.7} />
      </mesh>
      {/* Rear Drive Wheel */}
      <mesh position={[0, 0.12, -0.6]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.12, 0.12, 0.2, 16]} />
        <meshStandardMaterial color="#020617" roughness={0.8} />
      </mesh>

      {/* 3. Operator Cabin */}
      <mesh position={[0, 0.85, -0.05]}>
        <boxGeometry args={[0.85, 0.5, 0.45]} />
        <meshStandardMaterial color="#1e293b" roughness={0.6} />
      </mesh>
      {/* Mini Steering Wheel */}
      <mesh position={[-0.2, 1.05, -0.15]} rotation={[0.4, 0, 0]}>
        <torusGeometry args={[0.1, 0.02, 8, 16]} />
        <meshStandardMaterial color="#0f172a" />
      </mesh>
      {/* Digital Display Console */}
      <mesh position={[0.1, 1.08, -0.12]} rotation={[0.5, 0, 0]}>
        <boxGeometry args={[0.16, 0.1, 0.02]} />
        <meshStandardMaterial color="#38bdf8" roughness={0.1} />
      </mesh>

      {/* 4. Overhead Guard Pillars & Canopy Roof (Peak at 2.30m) */}
      {[-0.48, 0.48].map((px, i) => (
        <group key={`post-${i}`}>
          <mesh position={[px, 1.55, -0.78]}>
            <boxGeometry args={[0.06, 1.5, 0.06]} />
            <meshStandardMaterial color="#0f172a" roughness={0.4} />
          </mesh>
          <mesh position={[px, 1.55, 0.05]}>
            <boxGeometry args={[0.06, 1.5, 0.06]} />
            <meshStandardMaterial color="#0f172a" roughness={0.4} />
          </mesh>
        </group>
      ))}
      <mesh position={[0, 2.27, -0.36]}>
        <boxGeometry args={[1.05, 0.05, 0.9]} />
        <meshStandardMaterial color="#0f172a" roughness={0.4} />
      </mesh>
      {/* Amber Beacon */}
      <mesh position={[0, 2.36, -0.7]}>
        <cylinderGeometry args={[0.04, 0.05, 0.1, 12]} />
        <meshStandardMaterial color="#f59e0b" roughness={0.2} />
      </mesh>

      {/* 5. Telescopic Mast */}
      {[-0.32, 0.32].map((mx, i) => (
        <mesh key={`mast-${i}`} position={[mx, 1.45, 0.35]}>
          <boxGeometry args={[0.08, 2.2, 0.12]} />
          <meshStandardMaterial color="#334155" roughness={0.4} metalness={0.5} />
        </mesh>
      ))}
      <mesh position={[0, 2.3, 0.35]}>
        <boxGeometry args={[0.72, 0.08, 0.1]} />
        <meshStandardMaterial color="#334155" roughness={0.4} metalness={0.5} />
      </mesh>
      <mesh position={[0, 1.3, 0.35]}>
        <boxGeometry args={[0.72, 0.08, 0.1]} />
        <meshStandardMaterial color="#334155" roughness={0.4} metalness={0.5} />
      </mesh>
      <mesh position={[0, 1.1, 0.32]}>
        <cylinderGeometry args={[0.04, 0.04, 1.8, 12]} />
        <meshStandardMaterial color="#0284c7" metalness={0.6} roughness={0.3} />
      </mesh>

      {/* 6. Fork Carriage & Steel Forks */}
      <mesh position={[0, 0.38, 0.42]}>
        <boxGeometry args={[0.74, 0.45, 0.05]} />
        <meshStandardMaterial color="#1e293b" roughness={0.5} />
      </mesh>
      <mesh position={[0, 0.72, 0.42]}>
        <boxGeometry args={[0.74, 0.28, 0.03]} />
        <meshStandardMaterial color="#1e293b" roughness={0.5} />
      </mesh>
      {[-0.22, 0.22].map((fx, i) => (
        <group key={`fork-${i}`} position={[fx, 0, 0.44]}>
          <mesh position={[0, 0.25, 0]}>
            <boxGeometry args={[0.1, 0.42, 0.04]} />
            <meshStandardMaterial color="#475569" metalness={0.6} roughness={0.4} />
          </mesh>
          <mesh position={[0, 0.04, 0.52]}>
            <boxGeometry args={[0.1, 0.045, 1.05]} />
            <meshStandardMaterial color="#475569" metalness={0.6} roughness={0.4} />
          </mesh>
        </group>
      ))}

      {/* Dimension & Model Badge */}
      {showHeightBadge && (
        <Html position={[0, 2.55, 0]} center>
          <div style={{
            background: 'rgba(15, 23, 42, 0.94)',
            color: 'white',
            padding: '3px 8px',
            borderRadius: '6px',
            fontSize: '10px',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
            border: '1px solid rgba(2, 132, 199, 0.6)',
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
          }}>
            <span style={{ color: '#38bdf8' }}>🚜</span>
            <span>{modelName}</span>
            <span style={{ opacity: 0.4 }}>|</span>
            <span style={{ color: '#34d399' }}>H: {targetHeight.toFixed(2)} m</span>
          </div>
        </Html>
      )}
    </group>
  );
};

// ---------------------------------------------------------------
// 3D Procedural Counterbalance Forklift (Toyota 8FBE Series)
// ---------------------------------------------------------------
const ProceduralForklift3D = ({ targetHeight = 2.10, showHeightBadge = false, modelName = 'Toyota Forklift 8FBE' }) => {
  const truckScale = targetHeight / 2.10;
  return (
    <group scale={[truckScale, truckScale, truckScale]}>
      {/* 1. Main Chassis & Counterbalance Body */}
      <mesh position={[0, 0.5, -0.2]}>
        <boxGeometry args={[1.1, 0.75, 1.3]} />
        <meshStandardMaterial color="#ea580c" roughness={0.3} metalness={0.2} />
      </mesh>
      <mesh position={[0, 0.55, -0.85]}>
        <sphereGeometry args={[0.55, 16, 12, 0, Math.PI * 2, 0, Math.PI * 0.5]} />
        <meshStandardMaterial color="#ea580c" roughness={0.4} />
      </mesh>
      <mesh position={[0, 0.15, -0.2]}>
        <boxGeometry args={[1.12, 0.18, 1.35]} />
        <meshStandardMaterial color="#0f172a" roughness={0.8} />
      </mesh>

      {/* 2. Industrial Wheels */}
      {[-0.56, 0.56].map((wx, i) => (
        <mesh key={`fw-${i}`} position={[wx, 0.22, 0.35]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.22, 0.22, 0.2, 16]} />
          <meshStandardMaterial color="#020617" roughness={0.8} />
        </mesh>
      ))}
      <mesh position={[0, 0.18, -0.7]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.18, 0.18, 0.24, 16]} />
        <meshStandardMaterial color="#020617" roughness={0.8} />
      </mesh>

      {/* 3. Driver Cab & Seat */}
      <mesh position={[0, 0.85, -0.2]}>
        <boxGeometry args={[0.9, 0.3, 0.8]} />
        <meshStandardMaterial color="#1e293b" roughness={0.7} />
      </mesh>
      <mesh position={[0, 1.05, -0.3]}>
        <boxGeometry args={[0.46, 0.35, 0.38]} />
        <meshStandardMaterial color="#0f172a" roughness={0.6} />
      </mesh>
      <mesh position={[0, 1.3, -0.46]}>
        <boxGeometry args={[0.46, 0.38, 0.08]} />
        <meshStandardMaterial color="#0f172a" roughness={0.6} />
      </mesh>
      <mesh position={[-0.15, 1.15, 0.08]} rotation={[0.45, 0, 0]}>
        <torusGeometry args={[0.11, 0.02, 8, 16]} />
        <meshStandardMaterial color="#0f172a" />
      </mesh>

      {/* 4. Overhead Guard */}
      {[-0.5, 0.5].map((px, i) => (
        <group key={`post-${i}`}>
          <mesh position={[px, 1.45, -0.6]}>
            <boxGeometry args={[0.06, 1.3, 0.06]} />
            <meshStandardMaterial color="#0f172a" roughness={0.4} />
          </mesh>
          <mesh position={[px, 1.45, 0.2]}>
            <boxGeometry args={[0.06, 1.3, 0.06]} />
            <meshStandardMaterial color="#0f172a" roughness={0.4} />
          </mesh>
        </group>
      ))}
      <mesh position={[0, 2.08, -0.2]}>
        <boxGeometry args={[1.08, 0.04, 0.95]} />
        <meshStandardMaterial color="#0f172a" roughness={0.4} />
      </mesh>
      <mesh position={[0, 2.16, -0.55]}>
        <cylinderGeometry args={[0.04, 0.05, 0.1, 12]} />
        <meshStandardMaterial color="#f59e0b" roughness={0.2} />
      </mesh>

      {/* 5. Front Mast */}
      {[-0.35, 0.35].map((mx, i) => (
        <mesh key={`mast-${i}`} position={[mx, 1.15, 0.5]}>
          <boxGeometry args={[0.08, 2.0, 0.12]} />
          <meshStandardMaterial color="#334155" roughness={0.4} metalness={0.5} />
        </mesh>
      ))}
      <mesh position={[0, 2.05, 0.5]}>
        <boxGeometry args={[0.78, 0.08, 0.1]} />
        <meshStandardMaterial color="#334155" metalness={0.5} />
      </mesh>

      {/* 6. Carriage and Forks */}
      <mesh position={[0, 0.35, 0.58]}>
        <boxGeometry args={[0.76, 0.45, 0.05]} />
        <meshStandardMaterial color="#1e293b" />
      </mesh>
      {[-0.24, 0.24].map((fx, i) => (
        <group key={`fork-${i}`} position={[fx, 0, 0.6]}>
          <mesh position={[0, 0.25, 0]}>
            <boxGeometry args={[0.1, 0.42, 0.04]} />
            <meshStandardMaterial color="#475569" metalness={0.6} />
          </mesh>
          <mesh position={[0, 0.04, 0.55]}>
            <boxGeometry args={[0.1, 0.045, 1.1]} />
            <meshStandardMaterial color="#475569" metalness={0.6} />
          </mesh>
        </group>
      ))}

      {/* Dimension Badge */}
      {showHeightBadge && (
        <Html position={[0, 2.35, 0]} center>
          <div style={{
            background: 'rgba(15, 23, 42, 0.94)',
            color: 'white',
            padding: '3px 8px',
            borderRadius: '6px',
            fontSize: '10px',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
            border: '1px solid rgba(234, 88, 12, 0.6)',
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
          }}>
            <span style={{ color: '#ea580c' }}>🚚</span>
            <span>{modelName}</span>
            <span style={{ opacity: 0.4 }}>|</span>
            <span style={{ color: '#34d399' }}>H: {targetHeight.toFixed(2)} m</span>
          </div>
        </Html>
      )}
    </group>
  );
};

// ---------------------------------------------------------------
// Helper: Area Type Icon for 3D Floating Badges
// ---------------------------------------------------------------
const getAreaIcon = (type) => {
  switch (type) {
    case 'storage_selective_rack': return '🏢';
    case 'storage_2way_shuttle':
    case 'storage_4way_shuttle': return '🔄';
    case 'conveyor': return '⚡';
    case 'operator_station': return '👷';
    case 'model_3d': return '🚚';
    case 'pickup': return '📥';
    case 'dropoff': return '📤';
    case 'storage':
    case 'storage_on_floor': return '📦';
    default: return '📍';
  }
};

// ---------------------------------------------------------------
// 3D Selection Ring & Border Highlight
// ---------------------------------------------------------------
const SelectionRing3D = ({ w, h, heightM, label, type, isSelected, isPositionLocked }) => {
  if (!isSelected) return null;

  const pad = 0.06;
  const ringW = w + pad;
  const ringH = h + pad;
  const cornerL = Math.min(0.45, Math.min(w, h) * 0.28);
  const cornerT = 0.035;

  return (
    <group position={[0, 0, 0]}>
      {/* 1. Floor Glow Fill */}
      <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[ringW, ringH]} />
        <meshBasicMaterial color="#3b82f6" transparent opacity={0.25} depthWrite={false} />
      </mesh>

      {/* 2. Outer Border Frame */}
      <mesh position={[0, 0.025, -ringH / 2]}>
        <boxGeometry args={[ringW, 0.015, cornerT]} />
        <meshBasicMaterial color="#2563eb" />
      </mesh>
      <mesh position={[0, 0.025, ringH / 2]}>
        <boxGeometry args={[ringW, 0.015, cornerT]} />
        <meshBasicMaterial color="#2563eb" />
      </mesh>
      <mesh position={[-ringW / 2, 0.025, 0]}>
        <boxGeometry args={[cornerT, 0.015, ringH]} />
        <meshBasicMaterial color="#2563eb" />
      </mesh>
      <mesh position={[ringW / 2, 0.025, 0]}>
        <boxGeometry args={[cornerT, 0.015, ringH]} />
        <meshBasicMaterial color="#2563eb" />
      </mesh>

      {/* 3. 4 Corner Accents (Cyan/Sky Accent) */}
      {/* NW */}
      <mesh position={[-ringW / 2 + cornerL / 2, 0.028, -ringH / 2]}>
        <boxGeometry args={[cornerL, 0.022, cornerT * 1.8]} />
        <meshBasicMaterial color="#60a5fa" />
      </mesh>
      <mesh position={[-ringW / 2, 0.028, -ringH / 2 + cornerL / 2]}>
        <boxGeometry args={[cornerT * 1.8, 0.022, cornerL]} />
        <meshBasicMaterial color="#60a5fa" />
      </mesh>
      {/* NE */}
      <mesh position={[ringW / 2 - cornerL / 2, 0.028, -ringH / 2]}>
        <boxGeometry args={[cornerL, 0.022, cornerT * 1.8]} />
        <meshBasicMaterial color="#60a5fa" />
      </mesh>
      <mesh position={[ringW / 2, 0.028, -ringH / 2 + cornerL / 2]}>
        <boxGeometry args={[cornerT * 1.8, 0.022, cornerL]} />
        <meshBasicMaterial color="#60a5fa" />
      </mesh>
      {/* SW */}
      <mesh position={[-ringW / 2 + cornerL / 2, 0.028, ringH / 2]}>
        <boxGeometry args={[cornerL, 0.022, cornerT * 1.8]} />
        <meshBasicMaterial color="#60a5fa" />
      </mesh>
      <mesh position={[-ringW / 2, 0.028, ringH / 2 - cornerL / 2]}>
        <boxGeometry args={[cornerT * 1.8, 0.022, cornerL]} />
        <meshBasicMaterial color="#60a5fa" />
      </mesh>
      {/* SE */}
      <mesh position={[ringW / 2 - cornerL / 2, 0.028, ringH / 2]}>
        <boxGeometry args={[cornerL, 0.022, cornerT * 1.8]} />
        <meshBasicMaterial color="#60a5fa" />
      </mesh>
      <mesh position={[ringW / 2, 0.028, ringH / 2 - cornerL / 2]}>
        <boxGeometry args={[cornerT * 1.8, 0.022, cornerL]} />
        <meshBasicMaterial color="#60a5fa" />
      </mesh>

      {/* 4. Floating 3D Badge */}
      <Html position={[0, heightM + 0.45, 0]} center style={{ pointerEvents: 'none' }}>
        <div style={{
          background: 'rgba(15, 23, 42, 0.94)',
          backdropFilter: 'blur(8px)',
          color: '#ffffff',
          padding: '5px 12px',
          borderRadius: '20px',
          border: '1.5px solid #3b82f6',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.45)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '12px',
          fontWeight: '600',
          whiteSpace: 'nowrap',
          userSelect: 'none'
        }}>
          <span style={{ fontSize: '15px' }}>{getAreaIcon(type)}</span>
          <span style={{ color: '#ffffff', fontWeight: 'bold' }}>{label || type}</span>
          <span style={{ opacity: 0.35 }}>|</span>
          <span style={{ color: '#93c5fd', fontSize: '11px' }}>
            {Math.round(w * 1000)} × {Math.round(h * 1000)} mm
          </span>
          <span style={{ opacity: 0.35 }}>|</span>
          <span style={{
            background: isPositionLocked ? 'rgba(245, 158, 11, 0.25)' : 'rgba(59, 130, 246, 0.25)',
            color: isPositionLocked ? '#fbbf24' : '#60a5fa',
            padding: '2px 7px',
            borderRadius: '10px',
            fontSize: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '3px'
          }}>
            {isPositionLocked ? '🔒 ล็อกตำแหน่ง' : '✋ ลากเพื่อย้าย'}
          </span>
        </div>
      </Html>
    </group>
  );
};

// ---------------------------------------------------------------
// Helper: Calculate 3D Height for Areas
// ---------------------------------------------------------------
const getAreaHeightM = (area, ph) => {
  if (area.type === 'storage_selective_rack') {
    const levels = area.rackConfig?.levels || 4;
    const airGap = (area.rackConfig?.airGap ?? 300) / 1000;
    const beamThick = (area.rackConfig?.beamThickness ?? 100) / 1000;
    return levels * (ph + airGap) + beamThick;
  }
  if (area.type === 'storage_2way_shuttle' || area.type === 'storage_4way_shuttle') {
    const levels = area.rackConfig?.levels || 4;
    const airGap = (area.rackConfig?.airGap ?? 100) / 1000;
    const shuttleGap = (area.rackConfig?.shuttleGap ?? 250) / 1000;
    return levels * (ph + airGap + shuttleGap);
  }
  if (area.type === 'conveyor') {
    return (area.conveyorConfig?.heightM || 0.62) + ph + 0.3;
  }
  if (area.type === 'operator_station') {
    return 1.85;
  }
  if (area.type === 'model_3d') {
    return area.modelConfig?.targetHeight || 2.4;
  }
  if (area.type === 'storage' || area.type === 'storage_on_floor') {
    const levels = area.rackConfig?.levels || 1;
    return levels * ph;
  }
  return area.stationConfig?.showPallet ? ph : 0.6;
};

// ---------------------------------------------------------------
// Main Map3DViewer
// ---------------------------------------------------------------
const Map3DViewer = ({
  mapData,
  setMapData,
  scaleCalibration,
  svgBlobUrl,
  selectedArea,
  setSelectedArea,
  selectedAreaIds,
  setSelectedAreaIds,
  selectedCorridor,
  setSelectedCorridor,
  selectedCorridorIds,
  setSelectedCorridorIds,
  updateAreaProp,
  deleteArea,
  deleteCorridor,
  showToast,
  isPositionLocked: propLocked,
  setIsPositionLocked: setPropLocked,
  showDimensions: propShowDims,
  setShowDimensions: setPropShowDims,
  cameraPreset
}) => {
  const [internalShowDimensions, setInternalShowDimensions] = useState(true);
  const showDimensions = propShowDims !== undefined ? propShowDims : internalShowDimensions;
  const setShowDimensions = setPropShowDims || setInternalShowDimensions;

  const [internalPositionLocked, setInternalPositionLocked] = useState(true);
  const isPositionLocked = propLocked !== undefined ? propLocked : internalPositionLocked;
  const setIsPositionLocked = setPropLocked || setInternalPositionLocked;

  const [activeCustomModel, setActiveCustomModel] = useState(null);
  const [hoveredAreaId, setHoveredAreaId] = useState(null);
  const [hoveredCorridorId, setHoveredCorridorId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const orbitControlsRef = useRef(null);
  const dragStateRef = useRef(null);
  const pointerDownPosRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    let isMounted = true;
    fetch('/api/models-3d?category=operator')
      .then(res => res.json())
      .then(json => {
        if (isMounted && json.status === 'success' && Array.isArray(json.data)) {
          const active = json.data.find(m => m.is_active);
          setActiveCustomModel(active || null);
        }
      })
      .catch(err => console.warn('Could not fetch active 3D models:', err));
    return () => { isMounted = false; };
  }, []);

  const ppm = scaleCalibration.pixelsPerMm || 1;
  const pxToM = ppm ? (1 / ppm) / 1000 : 1;
  // All area coordinates are stored in 800x600 canvas space
  // cx/cy = center of canvas = origin for 3D scene
  const cx = 400;
  const cy = 300;

  const mapTo3D = useCallback((x, y, w, h) => {
    const w3 = w * pxToM;
    const h3 = h * pxToM;
    const x3 = (x + w / 2 - cx) * pxToM;
    const z3 = (y + h / 2 - cy) * pxToM;
    return { x: x3, z: z3, w: w3, h: h3 };
  }, [pxToM, cx, cy]);

  // Calculate scene bounds in 3D meters to set optimal camera distance
  const sceneBounds = useMemo(() => {
    let minX = -400 * pxToM;
    let maxX = 400 * pxToM;
    let minZ = -300 * pxToM;
    let maxZ = 300 * pxToM;

    const allItems = [...(mapData.areas || []), ...(mapData.corridors || [])];
    if (allItems.length > 0) {
      let aMinX = Infinity, aMaxX = -Infinity, aMinZ = Infinity, aMaxZ = -Infinity;
      allItems.forEach(a => {
        const { x, z, w, h } = mapTo3D(a.x, a.y, a.w, a.h);
        aMinX = Math.min(aMinX, x - w / 2);
        aMaxX = Math.max(aMaxX, x + w / 2);
        aMinZ = Math.min(aMinZ, z - h / 2);
        aMaxZ = Math.max(aMaxZ, z + h / 2);
      });
      if (aMinX !== Infinity && isFinite(aMinX)) {
        minX = aMinX; maxX = aMaxX; minZ = aMinZ; maxZ = aMaxZ;
      }
    }

    const spanX = Math.max(maxX - minX, 10);
    const spanZ = Math.max(maxZ - minZ, 10);
    const centerX = (minX + maxX) / 2;
    const centerZ = (minZ + maxZ) / 2;
    const maxDim = Math.max(spanX, spanZ, 18);

    return { centerX, centerZ, maxDim };
  }, [mapData.areas, mapData.corridors, mapTo3D, pxToM]);

  const palletTypes = mapData.palletTypes?.length ? mapData.palletTypes : [
    { id: 'default', ...(mapData.palletSize || { width: 1000, length: 1200 }), name: 'Standard Pallet', color: '#0ea5e9' }
  ];
  const defaultPt = mapData.palletTypes?.find(p => p.isPrimary) || palletTypes[0];
  const pw = (defaultPt.width || 1000) / 1000;
  const pl = (defaultPt.length || 1200) / 1000;
  const defaultTotalHeightMm = defaultPt.totalHeight || (defaultPt.height > 250 ? defaultPt.height : (mapData.palletSize?.totalHeight || 1500));
  const ph = defaultTotalHeightMm / 1000;
  const palletBrand = defaultPt.brand || mapData.palletSize?.brand || 'timber';
  const palletBaseColor = palletBrand === 'loscam' ? '#b91c1c' : palletBrand === 'chep' ? '#1d4ed8' : '#d97706';

  // Layout image source – prefer SVG data URI, fallback to uploaded image
  const layoutSrc = svgBlobUrl || mapData.bgImageData || null;

  // ---------------------------------------------------------------
  // Dragging & Selection Handlers
  // ---------------------------------------------------------------
  const handleAreaClick = useCallback((area, e) => {
    const btn = e.nativeEvent ? e.nativeEvent.button : (e.button ?? 0);
    if (btn !== 0) return;

    e.stopPropagation();

    if (setSelectedArea) setSelectedArea(area);
    if (setSelectedAreaIds) setSelectedAreaIds([area.id]);
    if (setSelectedCorridor) setSelectedCorridor(null);
    if (setSelectedCorridorIds) setSelectedCorridorIds([]);
  }, [setSelectedArea, setSelectedAreaIds, setSelectedCorridor, setSelectedCorridorIds]);

  const handleDragEnd = useCallback(() => {
    if (orbitControlsRef.current) {
      orbitControlsRef.current.enabled = true;
    }
    document.body.style.cursor = 'default';
    setIsDragging(false);
    const lastDrag = dragStateRef.current;
    if (lastDrag?.hasMoved) {
      if (showToast) {
        showToast(lastDrag.isCorridor ? 'ย้ายตำแหน่งทางเดินสำเร็จ' : 'ย้ายตำแหน่งพื้นที่สำเร็จ', 'success');
      }
    }
    dragStateRef.current = null;
  }, [showToast]);

  const handleStartDrag = useCallback((area, e) => {
    // Only left-click handles selection and dragging!
    // Right-click is preserved for OrbitControls panning the layout
    const btn = e.nativeEvent ? e.nativeEvent.button : (e.button ?? 0);
    if (btn !== 0) return;

    e.stopPropagation();

    // Select the area so user can view/edit in the sidebar
    if (setSelectedArea) setSelectedArea(area);
    if (setSelectedAreaIds) setSelectedAreaIds([area.id]);
    if (setSelectedCorridor) setSelectedCorridor(null);
    if (setSelectedCorridorIds) setSelectedCorridorIds([]);

    // If position is locked, do not allow dragging
    if (isPositionLocked) return;

    if (dragStateRef.current) {
      handleDragEnd();
    }

    if (orbitControlsRef.current) {
      orbitControlsRef.current.enabled = false;
    }
    const { x, z, w, h } = mapTo3D(area.x, area.y, area.w, area.h);
    let floorHitX = e.point.x;
    let floorHitZ = e.point.z;
    if (e.ray && Math.abs(e.ray.direction.y) > 0.0001) {
      const dist = -e.ray.origin.y / e.ray.direction.y;
      floorHitX = e.ray.origin.x + e.ray.direction.x * dist;
      floorHitZ = e.ray.origin.z + e.ray.direction.z * dist;
    }
    dragStateRef.current = {
      isCorridor: false,
      areaId: area.id,
      start2Dx: area.x,
      start2Dy: area.y,
      lastUpdated2Dx: area.x,
      lastUpdated2Dy: area.y,
      startX: x,
      startZ: z,
      offsetX: x - floorHitX,
      offsetZ: z - floorHitZ,
      hasMoved: false,
      itemW: area.w,
      itemH: area.h,
    };
    setIsDragging(true);
    document.body.style.cursor = 'grabbing';
  }, [mapTo3D, setSelectedArea, setSelectedAreaIds, setSelectedCorridor, setSelectedCorridorIds, isPositionLocked, handleDragEnd]);

  const handleCorridorClick = useCallback((cor, e) => {
    const btn = e.nativeEvent ? e.nativeEvent.button : (e.button ?? 0);
    if (btn !== 0) return;

    e.stopPropagation();

    if (setSelectedCorridor) setSelectedCorridor(cor);
    if (setSelectedCorridorIds) setSelectedCorridorIds([cor.id]);
    if (setSelectedArea) setSelectedArea(null);
    if (setSelectedAreaIds) setSelectedAreaIds([]);
  }, [setSelectedCorridor, setSelectedCorridorIds, setSelectedArea, setSelectedAreaIds]);

  const handleStartDragCorridor = useCallback((cor, e) => {
    // Only left-click handles selection and dragging!
    const btn = e.nativeEvent ? e.nativeEvent.button : (e.button ?? 0);
    if (btn !== 0) return;

    e.stopPropagation();

    if (setSelectedCorridor) setSelectedCorridor(cor);
    if (setSelectedCorridorIds) setSelectedCorridorIds([cor.id]);
    if (setSelectedArea) setSelectedArea(null);
    if (setSelectedAreaIds) setSelectedAreaIds([]);

    if (isPositionLocked) return;

    if (dragStateRef.current) {
      handleDragEnd();
    }

    if (orbitControlsRef.current) {
      orbitControlsRef.current.enabled = false;
    }
    const { x, z, w, h } = mapTo3D(cor.x, cor.y, cor.w, cor.h);
    let floorHitX = e.point.x;
    let floorHitZ = e.point.z;
    if (e.ray && Math.abs(e.ray.direction.y) > 0.0001) {
      const dist = -e.ray.origin.y / e.ray.direction.y;
      floorHitX = e.ray.origin.x + e.ray.direction.x * dist;
      floorHitZ = e.ray.origin.z + e.ray.direction.z * dist;
    }
    dragStateRef.current = {
      isCorridor: true,
      corridorId: cor.id,
      start2Dx: cor.x,
      start2Dy: cor.y,
      lastUpdated2Dx: cor.x,
      lastUpdated2Dy: cor.y,
      startX: x,
      startZ: z,
      offsetX: x - floorHitX,
      offsetZ: z - floorHitZ,
      hasMoved: false,
      itemW: cor.w,
      itemH: cor.h,
    };
    setIsDragging(true);
    document.body.style.cursor = 'grabbing';
  }, [mapTo3D, setSelectedCorridor, setSelectedCorridorIds, setSelectedArea, setSelectedAreaIds, isPositionLocked, handleDragEnd]);

  const handleFloorPointerMove = useCallback((e) => {
    const currentDrag = dragStateRef.current;
    if (!currentDrag) return;

    let floorHitX = e.point.x;
    let floorHitZ = e.point.z;
    if (e.ray && Math.abs(e.ray.direction.y) > 0.0001) {
      const dist = -e.ray.origin.y / e.ray.direction.y;
      floorHitX = e.ray.origin.x + e.ray.direction.x * dist;
      floorHitZ = e.ray.origin.z + e.ray.direction.z * dist;
    }

    const targetX = floorHitX + currentDrag.offsetX;
    const targetZ = floorHitZ + currentDrag.offsetZ;

    const new2Dx = Math.round((targetX / pxToM) + cx - currentDrag.itemW / 2);
    const new2Dy = Math.round((targetZ / pxToM) + cy - currentDrag.itemH / 2);

    if (currentDrag.lastUpdated2Dx === new2Dx && currentDrag.lastUpdated2Dy === new2Dy) {
      return;
    }
    currentDrag.lastUpdated2Dx = new2Dx;
    currentDrag.lastUpdated2Dy = new2Dy;

    const dx = Math.abs(new2Dx - currentDrag.start2Dx);
    const dy = Math.abs(new2Dy - currentDrag.start2Dy);
    if (dx > 2 || dy > 2) {
      currentDrag.hasMoved = true;
    }

    const { isCorridor, corridorId: targetCorridorId, areaId: targetAreaId } = currentDrag;

    if (isCorridor) {
      if (setMapData && targetCorridorId) {
        setMapData(prev => {
          if (!prev || !prev.corridors) return prev;
          return {
            ...prev,
            corridors: prev.corridors.map(c => (c && c.id === targetCorridorId ? { ...c, x: new2Dx, y: new2Dy } : c))
          };
        });
      }
      if (setSelectedCorridor && targetCorridorId) {
        setSelectedCorridor(prev => (prev && prev.id === targetCorridorId ? { ...prev, x: new2Dx, y: new2Dy } : prev));
      }
    } else {
      if (setMapData && targetAreaId) {
        setMapData(prev => {
          if (!prev || !prev.areas) return prev;
          return {
            ...prev,
            areas: prev.areas.map(a => (a && a.id === targetAreaId ? { ...a, x: new2Dx, y: new2Dy } : a))
          };
        });
      }
      if (setSelectedArea && targetAreaId) {
        setSelectedArea(prev => (prev && prev.id === targetAreaId ? { ...prev, x: new2Dx, y: new2Dy } : prev));
      }
    }
  }, [pxToM, cx, cy, setMapData, setSelectedArea, setSelectedCorridor]);

  const handleDeselectAll = useCallback(() => {
    if (setSelectedArea) setSelectedArea(null);
    if (setSelectedAreaIds) setSelectedAreaIds([]);
    if (setSelectedCorridor) setSelectedCorridor(null);
    if (setSelectedCorridorIds) setSelectedCorridorIds([]);
  }, [setSelectedArea, setSelectedAreaIds, setSelectedCorridor, setSelectedCorridorIds]);

  useEffect(() => {
    const onWindowPointerUp = () => {
      if (dragStateRef.current) {
        handleDragEnd();
      }
    };
    window.addEventListener('pointerup', onWindowPointerUp);
    return () => {
      window.removeEventListener('pointerup', onWindowPointerUp);
    };
  }, [handleDragEnd]);

  // Helper to render interactive wrapper around area visual contents
  const renderInteractiveArea = (area, visualContent, x, z, w, h, heightM) => {
    const isSelected = selectedArea?.id === area.id || selectedAreaIds?.includes(area.id);
    const isHovered = hoveredAreaId === area.id;

    return (
      <group
        key={area.id}
        position={[x, 0, z]}
        onClick={(e) => handleAreaClick(area, e)}
        onPointerDown={(e) => handleStartDrag(area, e)}
        onPointerMove={(e) => {
          if (dragStateRef.current) {
            e.stopPropagation();
            handleFloorPointerMove(e);
          }
        }}
        onPointerOver={(e) => {
          e.stopPropagation();
          setHoveredAreaId(area.id);
          if (!dragStateRef.current) {
            document.body.style.cursor = isPositionLocked ? 'pointer' : 'grab';
          }
        }}
        onPointerOut={(e) => {
          if (hoveredAreaId === area.id) {
            setHoveredAreaId(null);
            if (!dragStateRef.current) document.body.style.cursor = 'default';
          }
        }}
      >
        {/* Invisible 3D Hitbox for easy pointer interaction and dragging */}
        <mesh
          position={[0, Math.max(heightM, 0.5) / 2, 0]}
          onClick={(e) => handleAreaClick(area, e)}
          onPointerDown={(e) => handleStartDrag(area, e)}
        >
          <boxGeometry args={[w, Math.max(heightM, 0.5), h]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>

        {/* Hover Highlight (soft blue) */}
        {isHovered && !isSelected && (
          <mesh position={[0, 0.018, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <planeGeometry args={[w * 1.01, h * 1.01]} />
            <meshBasicMaterial color="#60a5fa" transparent opacity={0.15} depthWrite={false} />
          </mesh>
        )}

        {/* Selection Ring, Corner Accents, and 3D Floating Info Badge */}
        <SelectionRing3D
          w={w}
          h={h}
          heightM={heightM}
          label={area.label || area.name}
          type={area.type}
          isSelected={isSelected}
          isPositionLocked={isPositionLocked}
        />

        {/* Visual 3D Content */}
        {visualContent}
      </group>
    );
  };

  return (
    <div
      className="w-full h-full rounded-lg overflow-hidden"
      style={{ position: 'relative', background: 'transparent' }}
      onContextMenu={(e) => e.preventDefault()}
    >

      {/* ---- Three.js Canvas ---- */}
      <Canvas
        gl={{ alpha: true, antialias: true }}
        camera={{ position: [0, 20, 20], fov: 50, near: 0.5, far: 50000 }}
        style={{ background: 'transparent' }}
      >
        <CameraAnimator 
          cameraPreset={cameraPreset} 
          orbitControlsRef={orbitControlsRef} 
          sceneBounds={sceneBounds} 
        />

        <ambientLight intensity={0.6} />
        <directionalLight position={[10, 20, 10]} intensity={0.8} castShadow />
        <Environment preset="city" />

        {/* Layout Floor */}
        <TexturePlane 
          src={layoutSrc} 
          bgW={800} 
          bgH={600}
          pxToM={pxToM}
          rotation={mapData.bgSettings?.rotation ?? 0}
        />

        {/* Ground grid */}
        <Grid infiniteGrid fadeDistance={20000} sectionColor="#cbd5e1" cellColor="#e2e8f0" />

        {/* Ground Raycast Plane for Floor Dragging & Deselecting */}
        <mesh
          position={[0, 0, 0]}
          rotation={[-Math.PI / 2, 0, 0]}
          onPointerDown={(e) => {
            const clientX = e.clientX || e.nativeEvent?.clientX || 0;
            const clientY = e.clientY || e.nativeEvent?.clientY || 0;
            pointerDownPosRef.current = { x: clientX, y: clientY };
          }}
          onPointerMove={handleFloorPointerMove}
          onPointerUp={handleDragEnd}
          onClick={(e) => {
            const btn = e.nativeEvent ? e.nativeEvent.button : (e.button ?? 0);
            if (btn === 0) {
              const clientX = e.clientX || e.nativeEvent?.clientX || 0;
              const clientY = e.clientY || e.nativeEvent?.clientY || 0;
              const dist = Math.hypot(clientX - pointerDownPosRef.current.x, clientY - pointerDownPosRef.current.y);
              if (dist < 6 && (!dragStateRef.current || !dragStateRef.current.hasMoved)) {
                handleDeselectAll();
              }
            }
          }}
        >
          <planeGeometry args={[1000, 1000]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>

        {/* Areas */}
        {mapData.areas && mapData.areas.map(area => {
          const { x, z, w, h } = mapTo3D(area.x, area.y, area.w, area.h);

          if (area.type === 'storage' || area.type === 'storage_on_floor') {
            const prefOri = area.rackConfig?.orientation || 'auto';
            let pDimX = pw, pDimZ = pl;
            if (prefOri === 'horizontal') {
              pDimX = Math.max(pw, pl); pDimZ = Math.min(pw, pl);
            } else if (prefOri === 'vertical') {
              pDimX = Math.min(pw, pl); pDimZ = Math.max(pw, pl);
            } else {
              const cap1 = Math.floor(w / pw) * Math.floor(h / pl);
              const cap2 = Math.floor(w / pl) * Math.floor(h / pw);
              if (cap2 > cap1) {
                pDimX = pl; pDimZ = pw;
              }
            }

            const levels = area.rackConfig?.levels || 1;
            const cols = Math.max(1, Math.floor(w / pDimX));
            const rows = Math.max(1, Math.floor(h / pDimZ));
            const pallets = [];
            const baseHeightM = 0.14;
            const cargoHeightM = Math.max(0.1, ph - baseHeightM);

            const palletStatus = area.rackConfig?.palletStatus || 'full';
            const emptySlots = area.rackConfig?.emptySlots || [];
            const emptyStack = area.rackConfig?.emptyPalletStackCount || levels;

            const spacingX = cols > 1 ? Math.max(0.04, (w - cols * pDimX) / (cols + 1)) : 0;
            const spacingZ = rows > 1 ? Math.max(0.04, (h - rows * pDimZ) / (rows + 1)) : 0;

            for (let r = 0; r < rows; r++) {
              for (let c = 0; c < cols; c++) {
                const px = cols === 1 ? 0 : (-w / 2) + spacingX * (c + 1) + pDimX * (c + 0.5);
                const pz = rows === 1 ? 0 : (-h / 2) + spacingZ * (r + 1) + pDimZ * (r + 0.5);
                const slotIdx = r * cols + c;
                const isSlotEmpty = palletStatus === 'empty' || (palletStatus === 'custom' && emptySlots.includes(slotIdx));

                if (isSlotEmpty) {
                  pallets.push(
                    <group key={`empty-p-${r}-${c}`} position={[px, 0, pz]}>
                      <PalletWithCargo3D
                        width={pDimX * 0.98}
                        length={pDimZ * 0.98}
                        baseHeight={baseHeightM}
                        baseColor={palletBaseColor}
                        yOffset={0}
                        isEmpty={true}
                        emptyStack={emptyStack}
                      />
                    </group>
                  );
                } else {
                  for (let lvl = 0; lvl < levels; lvl++) {
                    const tierY = lvl * ph;
                    pallets.push(
                      <group key={`p-${r}-${c}-${lvl}`} position={[px, tierY, pz]}>
                        <PalletWithCargo3D
                          width={pDimX * 0.98}
                          length={pDimZ * 0.98}
                          baseHeight={baseHeightM}
                          cargoHeight={cargoHeightM}
                          baseColor={palletBaseColor}
                          yOffset={0}
                          isEmpty={false}
                        />
                      </group>
                    );
                  }
                }
              }
            }
            const visualContent = (
              <group>
                {/* Floor Staging Zone Marking Pad with soft indigo/purple border */}
                <mesh position={[0, 0.005, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                  <planeGeometry args={[w, h]} />
                  <meshBasicMaterial color="#8b5cf6" transparent opacity={0.12} />
                </mesh>
                <mesh position={[0, 0.008, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                  <planeGeometry args={[w * 0.98, h * 0.98]} />
                  <meshBasicMaterial color="#f59e0b" transparent opacity={0.06} />
                </mesh>
                {pallets}
              </group>
            );
            return renderInteractiveArea(area, visualContent, x, z, w, h, levels * ph);
          } else if (area.type === 'storage_selective_rack') {
            const ppb = area.rackConfig?.palletsPerBay || 2;
            const margin = (area.rackConfig?.margin ?? 100) / 1000;
            const levels = area.rackConfig?.levels || 4;
            const beamThick = (area.rackConfig?.beamThickness ?? 100) / 1000;
            const airGap = (area.rackConfig?.airGap ?? 300) / 1000;

            const backToBack = (area.rackConfig?.backToBack ?? 150) / 1000;
            const aisleWidth = (area.rackConfig?.aisleWidth ?? 3000) / 1000;
            const prefOri = area.rackConfig?.orientation || 'auto';
            const hasBottomBeam = area.rackConfig?.hasBottomBeam !== false;
            const singleRowStart = !!area.rackConfig?.singleRowStart;
            
            // Passage Way Config
            const passageEnabled = !!area.rackConfig?.passageEnabled;
            const passageLevels = area.rackConfig?.passageLevels || 2;
            const passageRowsRaw = area.rackConfig?.passageRows || '';
            const passageBaysRaw = area.rackConfig?.passageBays || '';

            const bayWidth = (ppb * pw) + ((ppb + 1) * margin);
            const blockSize = (2 * pl) + backToBack + aisleWidth;

            const calcCap = (ew, eh) => {
              let bays = Math.floor((ew - beamThick) / (bayWidth + beamThick));
              if (bays < 0) bays = 0;
              let rows = 0;
              let remH = eh;
              if (singleRowStart) {
                if (remH >= pl) {
                  rows += 1;
                  remH -= (pl + aisleWidth);
                }
              }
              if (remH >= pl) {
                const numBlocks = Math.floor(remH / blockSize);
                rows += numBlocks * 2;
                remH -= numBlocks * blockSize;
                if (remH >= (2 * pl) + backToBack) rows += 2;
                else if (remH >= pl) rows += 1;
              }
              return { bays, rows, total: bays * ppb * levels * rows };
            };

            const capH = calcCap(w, h);
            const capV = calcCap(h, w);
            
            let isVert = false;
            let bays, rows, total;
            
            if (prefOri === 'horizontal' || (prefOri === 'auto' && capH.total >= capV.total)) {
              bays = capH.bays; rows = capH.rows; total = capH.total;
            } else {
              bays = capV.bays; rows = capV.rows; total = capV.total;
              isVert = true;
            }
            
            if (bays === 0 || rows === 0) return null;

            // Parse passage targets
            const parseTargets = (raw) => {
              if (!raw || raw.trim() === '') return null;
              const parts = raw.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
              return parts.length > 0 ? parts : null;
            };
            
            const targetPassageRows = parseTargets(passageRowsRaw);
            const targetPassageBays = parseTargets(passageBaysRaw);
            
            const isRowPassage = (rIndex) => {
               if (!passageEnabled) return false;
               if (!targetPassageRows) return true; // All rows if empty
               return targetPassageRows.includes(rIndex + 1); // User inputs 1-based index
            };
            
            const isBayPassage = (cIndex) => {
               if (!passageEnabled) return false;
               if (!targetPassageBays) {
                  const mid = Math.floor((bays - 1) / 2);
                  return cIndex === mid; // Middle bay if empty
               }
               return targetPassageBays.includes(cIndex + 1); // User inputs 1-based index
            };

            // Recalculate total pallets
            if (passageEnabled) {
                let missingPallets = 0;
                for (let r = 0; r < rows; r++) {
                   if (isRowPassage(r)) {
                       for (let c = 0; c < bays; c++) {
                           if (isBayPassage(c)) {
                               missingPallets += ppb * Math.min(levels, passageLevels);
                           }
                       }
                   }
                }
                total -= missingPallets;
            }

            const getPalletForLevel = (lvl) => {
              const ptId = area.rackConfig?.levelPalletTypes?.[lvl];
              if (ptId) {
                const pt = palletTypes.find(p => p.id === ptId);
                if (pt) return pt;
              }
              return defaultPt;
            };

            let rackHeight = hasBottomBeam ? beamThick : 0;
            const levelHeights = [];
            for (let lvl = 0; lvl < levels; lvl++) {
              const pt = getPalletForLevel(lvl);
              const lph = (pt.height || 1400) / 1000;
              levelHeights.push(lph);
              let h = lph;
              if (lvl < levels - 1) h += airGap;
              rackHeight += h;
            }
            const startX = -w / 2;
            const startZ = -h / 2;

            const safeRacksPush = (racksArr, ...items) => {
              for (const item of items) racksArr.push(item);
            };

            const racks = [];
            const pushRack = (...items) => safeRacksPush(racks, ...items);
            
            const palletInstances = {}; // lvl -> {positions, args, color}
            const columnPositions = [];
            const frontBeamPositions = [];
            const backBeamPositions = [];

            for (let r = 0; r < rows; r++) {
              let localRow = 0;
              if (!singleRowStart) {
                const blockIndex = Math.floor(r / 2);
                const isSecond = r % 2 === 1;
                localRow = blockIndex * blockSize + (isSecond ? pl + backToBack : 0);
              } else {
                if (r === 0) {
                  localRow = 0;
                } else {
                  const rAdj = r - 1;
                  const blockIndex = Math.floor(rAdj / 2);
                  const isSecond = rAdj % 2 === 1;
                  localRow = pl + aisleWidth + blockIndex * blockSize + (isSecond ? pl + backToBack : 0);
                }
              }
              
              const rowHasPassage = isRowPassage(r);
              
              // Uprights (Columns)
              for (let c = 0; c <= bays; c++) {
                const localCol = c * (bayWidth + beamThick);
                const colX = isVert ? startX + localRow + pl / 2 : startX + localCol + beamThick / 2;
                const colZ = isVert ? startZ + localCol + beamThick / 2 : startZ + localRow + pl / 2;
                columnPositions.push(colX, rackHeight / 2, colZ);
              }
              
              for (let c = 0; c < bays; c++) {
                const localBayStart = c * (bayWidth + beamThick) + beamThick;
                const bayHasPassage = rowHasPassage && isBayPassage(c);
                
                for (let lvl = 0; lvl < levels; lvl++) {
                  if (bayHasPassage && lvl < passageLevels) {
                    continue; // Skip drawing beams and pallets for passage way
                  }
                  
                  let levelY = hasBottomBeam ? beamThick : 0;
                  if (lvl === 0) levelY = 0;
                  else {
                    for (let l = 0; l < lvl; l++) {
                      levelY += levelHeights[l] + airGap;
                    }
                  }
                  const beamY = levelY + beamThick / 2;
                  const beamX = isVert ? startX + localRow + pl / 2 : startX + localBayStart + bayWidth / 2;
                  const beamZ = isVert ? startZ + localBayStart + bayWidth / 2 : startZ + localRow + pl / 2;
                  const fOffset = -pl/2 + beamThick/2;
                  const bOffset = pl/2 - beamThick/2;
                  
                  if (hasBottomBeam || lvl > 0) {
                    if (isVert) {
                      frontBeamPositions.push(beamX + fOffset, beamY, beamZ);
                      backBeamPositions.push(beamX + bOffset, beamY, beamZ);
                    } else {
                      frontBeamPositions.push(beamX, beamY, beamZ + fOffset);
                      backBeamPositions.push(beamX, beamY, beamZ + bOffset);
                    }
                  }

                  // Pallets
                  for (let p = 0; p < ppb; p++) {
                    const localPallet = localBayStart + margin + p * (pw + margin) + pw / 2;
                    const palletX = isVert ? startX + localRow + pl / 2 : startX + localPallet;
                    const palletZ = isVert ? startZ + localPallet : startZ + localRow + pl / 2;
                    const pt = getPalletForLevel(lvl);
                    const lph = levelHeights[lvl];
                    const lpw = pt.width / 1000;
                    const lpl = pt.length / 1000;
                    const lvlBaseColor = pt.brand === 'loscam' ? '#b91c1c' : pt.brand === 'chep' ? '#1d4ed8' : palletBaseColor;
                    const palletY = levelY + (lvl === 0 && !hasBottomBeam ? 0 : beamThick) + lph / 2;
                    const pArgs = isVert ? [lpl, lph, lpw] : [lpw, lph, lpl];
                    
                    if (!palletInstances[lvl]) {
                       palletInstances[lvl] = { positions: [], args: pArgs, baseColor: lvlBaseColor };
                    }
                    palletInstances[lvl].positions.push(palletX, palletY, palletZ);
                  }
                }
              }
            }

            const colArgs = isVert ? [pl, rackHeight, beamThick] : [beamThick, rackHeight, pl];
            const frontBeamArgs = isVert ? [beamThick, beamThick, bayWidth] : [bayWidth, beamThick, beamThick];
            const backBeamArgs = isVert ? [beamThick, beamThick, bayWidth] : [bayWidth, beamThick, beamThick];

            if (columnPositions.length > 0) {
               pushRack(
                 <InstancedBoxes 
                    key={`inst-col-${area.id}`} 
                    count={columnPositions.length / 3} 
                    positions={columnPositions} 
                    args={colArgs} 
                    color="#1e40af" 
                 />
               );
            }
            if (frontBeamPositions.length > 0) {
               pushRack(
                 <InstancedBoxes 
                    key={`inst-fbeam-${area.id}`} 
                    count={frontBeamPositions.length / 3} 
                    positions={frontBeamPositions} 
                    args={frontBeamArgs} 
                    color="#f97316" 
                 />
               );
            }
            if (backBeamPositions.length > 0) {
               pushRack(
                 <InstancedBoxes 
                    key={`inst-bbeam-${area.id}`} 
                    count={backBeamPositions.length / 3} 
                    positions={backBeamPositions} 
                    args={backBeamArgs} 
                    color="#f97316" 
                 />
               );
            }

            Object.values(palletInstances).forEach((pi, idx) => {
                if (pi.positions.length > 0) {
                    pushRack(
                       <InstancedPallets 
                         key={`inst-pal-${area.id}-${idx}`} 
                         count={pi.positions.length / 3} 
                         positions={pi.positions} 
                         args={pi.args} 
                         baseColor={pi.baseColor || palletBaseColor} 
                       />
                    );
                }
            });

            if (showDimensions) {
              const dimW_Z = startZ + h + 0.3;
              pushRack(
                <group key={`dim-w-${Math.random()}`}>
                  <mesh position={[0, 0.01, dimW_Z]}>
                    <boxGeometry args={[w, 0.02, 0.02]} />
                    <meshBasicMaterial color="#10b981" />
                  </mesh>
                  <mesh position={[-w/2, 0.01, dimW_Z - 0.1]}>
                    <boxGeometry args={[0.02, 0.02, 0.2]} />
                    <meshBasicMaterial color="#10b981" />
                  </mesh>
                  <mesh position={[w/2, 0.01, dimW_Z - 0.1]}>
                    <boxGeometry args={[0.02, 0.02, 0.2]} />
                    <meshBasicMaterial color="#10b981" />
                  </mesh>
                  <Html position={[0, 0.01, dimW_Z + 0.1]} center>
                    <div style={{
                      background: 'rgba(16, 185, 129, 0.9)',
                      color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap', pointerEvents: 'none', userSelect: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                    }}>
                      W: {Math.round(w * 1000)} mm
                    </div>
                  </Html>
                </group>
              );

              const dimL_X = startX - 0.7;
              pushRack(
                <group key={`dim-l-${Math.random()}`}>
                  <mesh position={[dimL_X, 0.01, 0]}>
                    <boxGeometry args={[0.02, 0.02, h]} />
                    <meshBasicMaterial color="#10b981" />
                  </mesh>
                  <mesh position={[dimL_X + 0.1, 0.01, -h/2]}>
                    <boxGeometry args={[0.2, 0.02, 0.02]} />
                    <meshBasicMaterial color="#10b981" />
                  </mesh>
                  <mesh position={[dimL_X + 0.1, 0.01, h/2]}>
                    <boxGeometry args={[0.2, 0.02, 0.02]} />
                    <meshBasicMaterial color="#10b981" />
                  </mesh>
                  <Html position={[dimL_X - 0.1, 0.01, 0]} center>
                    <div style={{
                      background: 'rgba(16, 185, 129, 0.9)',
                      color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap', pointerEvents: 'none', userSelect: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                    }}>
                      L: {Math.round(h * 1000)} mm
                    </div>
                  </Html>
                </group>
              );

              const dimH_X = startX - 0.7;
              const dimH_Z = -h/2;
              pushRack(
                <group key={`dim-h-${Math.random()}`}>
                  <mesh position={[dimH_X, rackHeight/2, dimH_Z]}>
                    <boxGeometry args={[0.02, rackHeight, 0.02]} />
                    <meshBasicMaterial color="#10b981" />
                  </mesh>
                  <mesh position={[dimH_X + 0.1, 0, dimH_Z]}>
                    <boxGeometry args={[0.2, 0.02, 0.02]} />
                    <meshBasicMaterial color="#10b981" />
                  </mesh>
                  <mesh position={[dimH_X + 0.1, rackHeight, dimH_Z]}>
                    <boxGeometry args={[0.2, 0.02, 0.02]} />
                    <meshBasicMaterial color="#10b981" />
                  </mesh>
                  <Html position={[dimH_X - 0.1, rackHeight/2, dimH_Z]} center>
                    <div style={{
                      background: 'rgba(16, 185, 129, 0.9)',
                      color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap', pointerEvents: 'none', userSelect: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                    }}>
                      H: {Math.round(rackHeight * 1000)} mm
                    </div>
                  </Html>
                </group>
              );
              
              pushRack(
                <Html 
                  key={`cap-label-${area.id}`} 
                  position={[Math.max(w/2 - 1, 0.5), 0.01, startZ + h + 0.5]} 
                  center
                >
                  <div style={{
                    background: 'rgba(139, 92, 246, 0.95)',
                    color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap', userSelect: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.2)'
                  }}>
                    {total} Pallets
                  </div>
                </Html>
              );
            }

            const visualContent = (
              <group>
                <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                  <planeGeometry args={[w, h]} />
                  <meshBasicMaterial color="#8b5cf6" transparent opacity={0.05} />
                </mesh>
                {racks}
              </group>
            );
            return renderInteractiveArea(area, visualContent, x, z, w, h, rackHeight || (levels * ph));
          } else if (area.type === 'storage_2way_shuttle' || area.type === 'storage_4way_shuttle') {
            const margin = (area.rackConfig?.margin ?? 100) / 1000;
            const levels = area.rackConfig?.levels || 4;
            const beamThick = (area.rackConfig?.beamThickness ?? 100) / 1000;
            const hasBottomBeam = area.rackConfig?.hasBottomBeam !== false;
            
            const laneWidth = pw + margin + beamThick;
            const laneDepth = pl + margin;
            
            let vTracks = area.rackConfig?.vTracks || 0;
            let hTracks = area.rackConfig?.hTracks || 0;
            
            const calcCap = (ew, eh, isVrt) => {
              let c = Math.floor(ew / laneWidth);
              let r = Math.floor(eh / laneDepth);
              
              let screenCols = isVrt ? r : c;
              let screenRows = isVrt ? c : r;
              
              let vT = vTracks >= screenCols ? 0 : vTracks;
              let hT = hTracks >= screenRows ? 0 : hTracks;
              
              let t = (screenCols - vT) * (screenRows - hT) * levels;
              if (t < 0) t = 0;
              
              const getIndices = (count, tracks) => {
                 const indices = [];
                 for (let i = 1; i <= tracks; i++) {
                   indices.push(Math.floor((count * i) / (tracks + 1)));
                 }
                 return indices;
              };
              
              let screenColIndices = getIndices(screenCols, vT);
              let screenRowIndices = getIndices(screenRows, hT);
              
              let rTrackIndices = isVrt ? screenColIndices : screenRowIndices;
              let cTrackIndices = isVrt ? screenRowIndices : screenColIndices;
              
              return { cols: c, rows: r, total: t, rTrackIndices, cTrackIndices };
            };
            
            const prefOri = area.rackConfig?.orientation || 'auto';
            let cols, rows, total, isVert = false, rTrackIndices, cTrackIndices;
            
            const capH = calcCap(w, h, false);
            const capV = calcCap(h, w, true);
            
            if (area.type === 'storage_4way_shuttle' || prefOri === 'horizontal' || (prefOri === 'auto' && capH.total >= capV.total)) {
              cols = capH.cols; rows = capH.rows; total = capH.total;
              rTrackIndices = capH.rTrackIndices; cTrackIndices = capH.cTrackIndices;
            } else {
              cols = capV.cols; rows = capV.rows; total = capV.total;
              rTrackIndices = capV.rTrackIndices; cTrackIndices = capV.cTrackIndices;
              isVert = true;
            }
            
            if (cols === 0 || rows === 0) return null;
            
            const getPalletForLevel = (lvl) => {
              const defaultPtId = area.rackConfig?.defaultPalletType || palletTypes[0]?.id;
              const defaultPt = palletTypes.find(p => p.id === defaultPtId) || { width: 1000, length: 1200, height: 1400, color: '#0ea5e9' };
              const ptId = area.rackConfig?.levelPalletTypes?.[lvl];
              if (ptId) {
                const pt = palletTypes.find(p => p.id === ptId);
                if (pt) return pt;
              }
              return defaultPt;
            };

            const airGap = (area.rackConfig?.airGap ?? 100) / 1000; // Clearance above pallet
            const shuttleGap = (area.rackConfig?.shuttleGap ?? 250) / 1000; // Space for shuttle car and rails
            let rackHeight = 0;
            const levelHeights = [];
            for (let lvl = 0; lvl < levels; lvl++) {
              const pt = getPalletForLevel(lvl);
              const lph = (pt.height || 1400) / 1000;
              levelHeights.push(lph);
              let h = lph;
              if (lvl < levels - 1) h += airGap;
              if (lvl === 0 && !hasBottomBeam) {
                 // No rails or beam on floor
              } else {
                 h += shuttleGap + beamThick; 
              }
              rackHeight += h;
            }
            
            const startX = -w / 2;
            const startZ = -h / 2;
                        const safeRacksPush = (racksArr, ...items) => {
              const hasNaN = (obj) => {
                if (typeof obj === 'number' && isNaN(obj)) return true;
                if (Array.isArray(obj)) return obj.some(hasNaN);
                if (obj && typeof obj === 'object') {
                  if (obj.props && obj.props.position && obj.props.position.some(isNaN)) return true;
                  if (obj.props && obj.props.args && obj.props.args.some(isNaN)) return true;
                  if (obj.props && obj.props.children) {
                     if (Array.isArray(obj.props.children)) {
                        return obj.props.children.some(child => hasNaN(child));
                     } else {
                        return hasNaN(obj.props.children);
                     }
                  }
                }
                return false;
              };
              for (const item of items) {
                if (hasNaN(item)) {
                  console.error('NaN detected in mesh:', item);
                  continue;
                }
                racksArr.push(item);
              }
            };

            const racks = [];
            const pushRack = (...items) => safeRacksPush(racks, ...items);
            const palletInstances = {}; // lvl -> {positions, args, color}
            
            for (let c = 0; c < cols; c++) {
              for (let r = 0; r < rows; r++) {
                const localX = c * laneWidth;
                const localZ = r * laneDepth;
                
                const railX = isVert ? startX + localZ + laneDepth / 2 : startX + localX + laneWidth / 2;
                const railZ = isVert ? startZ + localX + laneWidth / 2 : startZ + localZ + laneDepth / 2;
                
                let currentY = 0;
                for (let lvl = 0; lvl < levels; lvl++) {
                  const pt = getPalletForLevel(lvl);
                  const lph = levelHeights[lvl];
                  const lpw = pt.width / 1000;
                  const lpl = pt.length / 1000;
                  const palletColor = pt.color || '#0ea5e9';
                  const pWidth = isVert ? lpw : lpl;
                  const pDepth = isVert ? lpl : lpw;
                  
                  const hasBeamThisLevel = (hasBottomBeam || lvl > 0);
                  
                  if (hasBeamThisLevel) {
                     // 1. Structural horizontal beam (orange) running left-to-right (perpendicular to lane)
                     const beamY = currentY + beamThick / 2;
                     
                     // 2. Silver rails (L-bracket style) running front-to-back
                     const railHeight = shuttleGap;
                     const railY = currentY + beamThick + railHeight / 2;
                     const railProtrusion = beamThick * 1.5; // Rail extends inward
                     
                     if (area.type === 'storage_2way_shuttle') {
                         const rThick = railProtrusion * 0.2;
                         if (isVert) {
                            pushRack(
                              // Left L-Rail
                              <group key={`s-rail-${c}-${r}-${lvl}-1`}>
                                {/* Vertical flange */}
                                <mesh position={[railX, railY, railZ - pWidth/2 + rThick/2]}>
                                  <boxGeometry args={[laneDepth, railHeight, rThick]} />
                                  <meshStandardMaterial color="#94a3b8" />
                                </mesh>
                                {/* Horizontal ledge */}
                                <mesh position={[railX, railY - railHeight/2 + rThick/2, railZ - pWidth/2 + railProtrusion/2]}>
                                  <boxGeometry args={[laneDepth, rThick, railProtrusion]} />
                                  <meshStandardMaterial color="#94a3b8" />
                                </mesh>
                              </group>,
                              // Right L-Rail
                              <group key={`s-rail-${c}-${r}-${lvl}-2`}>
                                <mesh position={[railX, railY, railZ + pWidth/2 - rThick/2]}>
                                  <boxGeometry args={[laneDepth, railHeight, rThick]} />
                                  <meshStandardMaterial color="#94a3b8" />
                                </mesh>
                                <mesh position={[railX, railY - railHeight/2 + rThick/2, railZ + pWidth/2 - railProtrusion/2]}>
                                  <boxGeometry args={[laneDepth, rThick, railProtrusion]} />
                                  <meshStandardMaterial color="#94a3b8" />
                                </mesh>
                              </group>,
                              // Front/Back entrance vs internal support beam
                              (r === 0 || r === rows - 1) ? (
                                <group key={`s-obrack-${c}-${r}-${lvl}`}>
                                  <mesh position={[railX - laneDepth/2 + beamThick/2, beamY, railZ - laneWidth/2 + beamThick]}>
                                    <boxGeometry args={[beamThick*1.5, beamThick*1.5, beamThick]} />
                                    <meshStandardMaterial color="#f97316" />
                                  </mesh>
                                  <mesh position={[railX - laneDepth/2 + beamThick/2, beamY, railZ + laneWidth/2 - beamThick]}>
                                    <boxGeometry args={[beamThick*1.5, beamThick*1.5, beamThick]} />
                                    <meshStandardMaterial color="#f97316" />
                                  </mesh>
                                </group>
                              ) : (
                                <mesh key={`s-obeam-${c}-${r}-${lvl}`} position={[railX - laneDepth/2 + beamThick/2, beamY, railZ]}>
                                  <boxGeometry args={[beamThick, beamThick, laneWidth]} />
                                  <meshStandardMaterial color="#f97316" />
                                </mesh>
                              )
                            );
                         } else {
                            pushRack(
                              // Left L-Rail
                              <group key={`s-rail-${c}-${r}-${lvl}-1`}>
                                <mesh position={[railX - pWidth/2 + rThick/2, railY, railZ]}>
                                  <boxGeometry args={[rThick, railHeight, laneDepth]} />
                                  <meshStandardMaterial color="#94a3b8" />
                                </mesh>
                                <mesh position={[railX - pWidth/2 + railProtrusion/2, railY - railHeight/2 + rThick/2, railZ]}>
                                  <boxGeometry args={[railProtrusion, rThick, laneDepth]} />
                                  <meshStandardMaterial color="#94a3b8" />
                                </mesh>
                              </group>,
                              // Right L-Rail
                              <group key={`s-rail-${c}-${r}-${lvl}-2`}>
                                <mesh position={[railX + pWidth/2 - rThick/2, railY, railZ]}>
                                  <boxGeometry args={[rThick, railHeight, laneDepth]} />
                                  <meshStandardMaterial color="#94a3b8" />
                                </mesh>
                                <mesh position={[railX + pWidth/2 - railProtrusion/2, railY - railHeight/2 + rThick/2, railZ]}>
                                  <boxGeometry args={[railProtrusion, rThick, laneDepth]} />
                                  <meshStandardMaterial color="#94a3b8" />
                                </mesh>
                              </group>,
                              // Front/Back entrance vs internal support beam
                              (r === 0 || r === rows - 1) ? (
                                <group key={`s-obrack-${c}-${r}-${lvl}`}>
                                  <mesh position={[railX - laneWidth/2 + beamThick, beamY, railZ - laneDepth/2 + beamThick/2]}>
                                    <boxGeometry args={[beamThick, beamThick*1.5, beamThick*1.5]} />
                                    <meshStandardMaterial color="#f97316" />
                                  </mesh>
                                  <mesh position={[railX + laneWidth/2 - beamThick, beamY, railZ - laneDepth/2 + beamThick/2]}>
                                    <boxGeometry args={[beamThick, beamThick*1.5, beamThick*1.5]} />
                                    <meshStandardMaterial color="#f97316" />
                                  </mesh>
                                </group>
                              ) : (
                                <mesh key={`s-obeam-${c}-${r}-${lvl}`} position={[railX, beamY, railZ - laneDepth/2 + beamThick/2]}>
                                  <boxGeometry args={[laneWidth, beamThick, beamThick]} />
                                  <meshStandardMaterial color="#f97316" />
                                </mesh>
                              )
                            );
                         }
                     } else {
                        // 4-way shuttle (simplified)
                         pushRack(
                           <group key={`4way-rail-${c}-${r}-${lvl}`}>
                               <mesh position={[railX, beamY, railZ]}>
                                 <boxGeometry args={[isVert ? laneDepth : laneWidth, beamThick, isVert ? laneWidth : laneDepth]} />
                                 <meshStandardMaterial color="#334155" transparent opacity={0.6} />
                               </mesh>
                           </group>
                         );
                     }
                  }
                  
                  // Draw upright frames (posts) for shuttle
                  if (lvl === 0) {
                      const postHeight = rackHeight;
                      const postY = postHeight / 2;
                      const postThick = beamThick * 0.8;
                      
                      if (isVert) {
                          // Posts along Z axis edges
                          pushRack(
                              // Front Left Post
                              <mesh key={`post-${c}-${r}-1`} position={[railX - laneDepth/2 + postThick/2, postY, railZ - laneWidth/2 + postThick/2]}>
                                  <boxGeometry args={[postThick, postHeight, postThick]} />
                                  <meshStandardMaterial color="#1e40af" />
                              </mesh>,
                              // Front Right Post
                              <mesh key={`post-${c}-${r}-2`} position={[railX - laneDepth/2 + postThick/2, postY, railZ + laneWidth/2 - postThick/2]}>
                                  <boxGeometry args={[postThick, postHeight, postThick]} />
                                  <meshStandardMaterial color="#1e40af" />
                              </mesh>
                          );
                      } else {
                          // Posts along X axis edges
                          pushRack(
                              // Front Left Post
                              <mesh key={`post-${c}-${r}-1`} position={[railX - laneWidth/2 + postThick/2, postY, railZ - laneDepth/2 + postThick/2]}>
                                  <boxGeometry args={[postThick, postHeight, postThick]} />
                                  <meshStandardMaterial color="#1e40af" />
                              </mesh>,
                              // Front Right Post
                              <mesh key={`post-${c}-${r}-2`} position={[railX + laneWidth/2 - postThick/2, postY, railZ - laneDepth/2 + postThick/2]}>
                                  <boxGeometry args={[postThick, postHeight, postThick]} />
                                  <meshStandardMaterial color="#1e40af" />
                              </mesh>
                          );
                      }
                  }

                  // Pallet
                  const isTrack = (cTrackIndices && cTrackIndices.includes(c)) || (rTrackIndices && rTrackIndices.includes(r));
                  const palletY = currentY + (hasBeamThisLevel ? beamThick + shuttleGap : 0) + lph / 2;
                  const pArgs = isVert ? [lpl, lph, lpw] : [lpw, lph, lpl];
                  const lvlBaseColor = pt.brand === 'loscam' ? '#b91c1c' : pt.brand === 'chep' ? '#1d4ed8' : palletBaseColor;
                  
                  if (!isTrack) {
                     if (!palletInstances[lvl]) {
                         palletInstances[lvl] = { positions: [], args: pArgs, baseColor: lvlBaseColor };
                     }
                     palletInstances[lvl].positions.push(railX, palletY, railZ);
                  }
                  
                  // Advance Y for next level
                  currentY += lph + airGap;
                  if (hasBeamThisLevel) {
                      currentY += beamThick + shuttleGap;
                  }
                }
              }
            }
            
            // Render Pallet Instances
            Object.values(palletInstances).forEach((pi, idx) => {
                if (pi.positions.length > 0) {
                    pushRack(
                       <InstancedPallets 
                         key={`inst-pal-${area.id}-${idx}`} 
                         count={pi.positions.length / 3} 
                         positions={pi.positions} 
                         args={pi.args} 
                         baseColor={pi.baseColor || palletBaseColor} 
                       />
                    );
                }
            });

            // Draw Lifts
            const lifts = area.rackConfig?.lifts;
            if (lifts && (lifts.top || lifts.bottom || lifts.left || lifts.right)) {
               const liftColor = '#86efac';
               const liftW = isVert ? laneDepth + 0.4 : laneWidth + 0.4;
               const liftD = isVert ? laneWidth + 0.4 : laneDepth + 0.4;
               const postThick = 0.1;
               const totalHeight = rackHeight + 1.0;
               
               const drawLift = (cx, cz, keyPrefix) => {
                  // Platform
                  pushRack(
                    <mesh key={`${keyPrefix}-plat`} position={[cx, 0.2, cz]}>
                      <boxGeometry args={[liftW, 0.1, liftD]} />
                      <meshStandardMaterial color="#475569" />
                    </mesh>
                  );
                  // 4 Posts
                  const dx = liftW/2 - postThick/2;
                  const dz = liftD/2 - postThick/2;
                  const py = totalHeight / 2;
                  const positions = [
                     [cx - dx, py, cz - dz],
                     [cx + dx, py, cz - dz],
                     [cx - dx, py, cz + dz],
                     [cx + dx, py, cz + dz]
                  ];
                  positions.forEach((pos, idx) => {
                     pushRack(
                        <mesh key={`${keyPrefix}-post-${idx}`} position={pos}>
                          <boxGeometry args={[postThick, totalHeight, postThick]} />
                          <meshStandardMaterial color={liftColor} />
                        </mesh>
                     );
                  });
               };
               
               // Top & Bottom (aligned with cTrackIndices)
               if (cTrackIndices && cTrackIndices.length > 0) {
                  cTrackIndices.forEach(c => {
                        let lx, lz;
                        if (!isVert) {
                           lx = startX + c * laneWidth + laneWidth/2;
                           if (lifts.top) drawLift(lx, startZ - liftD/2, `lift-t-${c}`);
                           if (lifts.bottom) drawLift(lx, startZ + rows * laneDepth + liftD/2, `lift-b-${c}`);
                        } else {
                           lz = startZ + c * laneWidth + laneWidth/2;
                           if (lifts.left) drawLift(startX - liftW/2, lz, `lift-l-${c}`);
                           if (lifts.right) drawLift(startX + rows * laneDepth + liftW/2, lz, `lift-r-${c}`);
                        }
                    });
               }
               if (rTrackIndices && rTrackIndices.length > 0) {
                  rTrackIndices.forEach(r => {
                     if (!isVert) {
                        const lz = startZ + r * laneDepth + laneDepth/2;
                        if (lifts.left) drawLift(startX - liftW/2, lz, `lift-l-${r}`);
                        if (lifts.right) drawLift(startX + cols * laneWidth + liftW/2, lz, `lift-r-${r}`);
                     } else {
                        const lx = startX + r * laneDepth + laneDepth/2;
                        if (lifts.top) drawLift(lx, startZ - liftD/2, `lift-t-${r}`);
                        if (lifts.bottom) drawLift(lx, startZ + cols * laneWidth + liftD/2, `lift-b-${r}`);
                     }
                  });
               }
            }

            if (showDimensions) { // Labels
            let dimW_X = 0, dimW_Z = h / 2 + 0.2;
            let dimL_X = w / 2 + 0.2, dimL_Z = 0;
            let dimH_X = -w / 2 - 0.2, dimH_Z = -h / 2;
            
            pushRack(
              <group key="shuttle-dims">
                <Html position={[dimW_X, 0, dimW_Z]} center>
                  <div style={{ background: 'rgba(239, 68, 68, 0.9)', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap', pointerEvents: 'none', userSelect: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
                    W: {Math.round(w * 1000)} mm
                  </div>
                </Html>
                <Html position={[dimL_X, 0, dimL_Z]} center>
                  <div style={{ background: 'rgba(59, 130, 246, 0.9)', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap', pointerEvents: 'none', userSelect: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
                    L: {Math.round(h * 1000)} mm
                  </div>
                </Html>
                <Html position={[dimH_X - 0.1, rackHeight/2, dimH_Z]} center>
                  <div style={{ background: 'rgba(16, 185, 129, 0.9)', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap', pointerEvents: 'none', userSelect: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
                    H: {Math.round(rackHeight * 1000)} mm
                  </div>
                </Html>
              </group>
            );
            
            pushRack(
                <Html key="shuttle-cap" position={[Math.max(w/2 - 1, 0.5), rackHeight + 0.5, 0]} center>
                  <div style={{
                    background: 'rgba(139, 92, 246, 0.9)',
                    color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap', userSelect: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.2)'
                  }}>
                    {total} Pallets
                  </div>
                </Html>
            );
            
            }
            const visualContent = (
              <group>
                <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                  <planeGeometry args={[w, h]} />
                  <meshBasicMaterial color={area.type === 'storage_4way_shuttle' ? "#eab308" : "#8b5cf6"} transparent opacity={0.05} />
                </mesh>
                {racks}
              </group>
            );
            return renderInteractiveArea(area, visualContent, x, z, w, h, rackHeight || (levels * ph));
          }
          
          // Other area types (pickup, dropoff, conveyor, operator_station)
          if (area.type === 'conveyor') {
            const isHoriz = w > h;
            const length = isHoriz ? w : h;
            const width = isHoriz ? h : w;
            
            const convHeight = area.conveyorConfig?.heightM || 0.62;
            const frameColor = "#3b82f6";
            const rollerColor = "#d1d5db";
            const guardColor = "#9ca3af";
            
            const rollerRadius = 0.035;
            const rollerSpacing = 0.15;
            const numRollers = Math.floor(length / rollerSpacing);
            const rollers = [];
            
            for (let i = 0; i < numRollers; i++) {
               const pos = -length/2 + (i + 0.5) * rollerSpacing;
               if (isHoriz) {
                 rollers.push(<mesh key={`roller-${i}`} position={[pos, convHeight, 0]} rotation={[Math.PI/2, 0, 0]}><cylinderGeometry args={[rollerRadius, rollerRadius, width - 0.1, 16]} /><meshStandardMaterial color={rollerColor} metalness={0.5} roughness={0.4} /></mesh>);
               } else {
                 rollers.push(<mesh key={`roller-${i}`} position={[0, convHeight, pos]} rotation={[0, 0, Math.PI/2]}><cylinderGeometry args={[rollerRadius, rollerRadius, width - 0.1, 16]} /><meshStandardMaterial color={rollerColor} metalness={0.5} roughness={0.4} /></mesh>);
               }
            }
            
            const numLegs = Math.max(2, Math.ceil(length / 2.0) + 1);
            const legs = [];
            for(let i=0; i<numLegs; i++) {
               const pos = -length/2 + 0.2 + (i * ((length - 0.4)/(numLegs-1 || 1)));
               if (isHoriz) {
                 legs.push(<mesh key={`leg1-${i}`} position={[pos, convHeight / 2, width/2 - 0.05]}><boxGeometry args={[0.06, convHeight, 0.06]} /><meshStandardMaterial color={frameColor} /></mesh>);
                 legs.push(<mesh key={`leg2-${i}`} position={[pos, convHeight / 2, -width/2 + 0.05]}><boxGeometry args={[0.06, convHeight, 0.06]} /><meshStandardMaterial color={frameColor} /></mesh>);
                 // Guardrail supports
                 legs.push(<mesh key={`gs1-${i}`} position={[pos, convHeight + 0.08, width/2 - 0.02]}><boxGeometry args={[0.02, 0.2, 0.02]} /><meshStandardMaterial color={guardColor} /></mesh>);
                 legs.push(<mesh key={`gs2-${i}`} position={[pos, convHeight + 0.08, -width/2 + 0.02]}><boxGeometry args={[0.02, 0.2, 0.02]} /><meshStandardMaterial color={guardColor} /></mesh>);
               } else {
                 legs.push(<mesh key={`leg1-${i}`} position={[width/2 - 0.05, convHeight / 2, pos]}><boxGeometry args={[0.06, convHeight, 0.06]} /><meshStandardMaterial color={frameColor} /></mesh>);
                 legs.push(<mesh key={`leg2-${i}`} position={[-width/2 + 0.05, convHeight / 2, pos]}><boxGeometry args={[0.06, convHeight, 0.06]} /><meshStandardMaterial color={frameColor} /></mesh>);
                 // Guardrail supports
                 legs.push(<mesh key={`gs1-${i}`} position={[width/2 - 0.02, convHeight + 0.08, pos]}><boxGeometry args={[0.02, 0.2, 0.02]} /><meshStandardMaterial color={guardColor} /></mesh>);
                 legs.push(<mesh key={`gs2-${i}`} position={[-width/2 + 0.02, convHeight + 0.08, pos]}><boxGeometry args={[0.02, 0.2, 0.02]} /><meshStandardMaterial color={guardColor} /></mesh>);
               }
            }

            const visualContent = (
              <group>
                {/* Frames */}
                   <mesh position={isHoriz ? [0, convHeight - 0.02, width/2 - 0.05] : [width/2 - 0.05, convHeight - 0.02, 0]}>
                     <boxGeometry args={isHoriz ? [length, 0.1, 0.05] : [0.05, 0.1, length]} />
                     <meshStandardMaterial color={frameColor} />
                   </mesh>
                   <mesh position={isHoriz ? [0, convHeight - 0.02, -width/2 + 0.05] : [-width/2 + 0.05, convHeight - 0.02, 0]}>
                     <boxGeometry args={isHoriz ? [length, 0.1, 0.05] : [0.05, 0.1, length]} />
                     <meshStandardMaterial color={frameColor} />
                   </mesh>
                   
                   {/* Guardrails */}
                   <mesh position={isHoriz ? [0, convHeight + 0.16, width/2 - 0.02] : [width/2 - 0.02, convHeight + 0.16, 0]}>
                     <boxGeometry args={isHoriz ? [length, 0.04, 0.04] : [0.04, 0.04, length]} />
                     <meshStandardMaterial color={guardColor} metalness={0.8} roughness={0.2} />
                   </mesh>
                   <mesh position={isHoriz ? [0, convHeight + 0.16, -width/2 + 0.02] : [-width/2 + 0.02, convHeight + 0.16, 0]}>
                     <boxGeometry args={isHoriz ? [length, 0.04, 0.04] : [0.04, 0.04, length]} />
                     <meshStandardMaterial color={guardColor} metalness={0.8} roughness={0.2} />
                   </mesh>
                   
                   {rollers}
                   {legs}

                    {/* Simulated Pallets & Cargo on Conveyor */}
                    {(() => {
                      const defaultPallet = mapData.palletTypes?.find(p => p.isPrimary) || mapData.palletTypes?.[0] || mapData.palletSize || {};
                      const pwM = (defaultPallet.width || 1000) / 1000;
                      const plM = (defaultPallet.length || 1200) / 1000;

                      // Pallet base height (e.g. 150mm = 0.15m)
                      const baseHeightMm = defaultPallet.baseHeight || (defaultPallet.height && defaultPallet.height <= 250 ? defaultPallet.height : 150);
                      const baseHeightM = baseHeightMm / 1000;

                      // Total height including cargo (e.g. 1750mm = 1.75m)
                      let totalHeightMm = 1500;
                      if (defaultPallet.totalHeight && defaultPallet.totalHeight > 250) {
                        totalHeightMm = defaultPallet.totalHeight;
                      } else if (defaultPallet.height && defaultPallet.height > 250) {
                        totalHeightMm = defaultPallet.height;
                      } else if (mapData.palletSize?.totalHeight && mapData.palletSize.totalHeight > 250) {
                        totalHeightMm = mapData.palletSize.totalHeight;
                      } else if (mapData.palletSize?.height && mapData.palletSize.height > 250) {
                        totalHeightMm = mapData.palletSize.height;
                      }

                      const totalHeightM = totalHeightMm / 1000;
                      // Cargo height is exact remainder of total load height minus base height
                      const cargoHeightM = Math.max(0.1, totalHeightM - baseHeightM);

                      let alongM = pwM, crossM = plM;
                      const prefOri = area.conveyorConfig?.palletOrientation || 'auto';
                      const sideGapMm = area.conveyorConfig?.sideGapMm !== undefined ? area.conveyorConfig.sideGapMm : 50;
                      const sideGapM = sideGapMm / 1000;

                      if (prefOri === 'width_leading') {
                        alongM = Math.min(pwM, plM); crossM = Math.max(pwM, plM);
                      } else if (prefOri === 'length_leading') {
                        alongM = Math.max(pwM, plM); crossM = Math.min(pwM, plM);
                      } else {
                        if (width >= plM + sideGapM * 2) {
                          crossM = plM; alongM = pwM;
                        } else if (width >= pwM + sideGapM * 2) {
                          crossM = pwM; alongM = plM;
                        } else {
                          crossM = Math.max(0.2, width - sideGapM * 2); alongM = pwM;
                        }
                      }
                      crossM = Math.min(Math.max(0.2, width - sideGapM * 2), crossM);

                      const gapM = 0.12;
                      let numPallets = Math.floor(length / (alongM + gapM));
                      if (numPallets <= 0 && length >= alongM) numPallets = 1;
                      if (numPallets * alongM > length) numPallets = Math.floor(length / alongM);
                      if (numPallets < 0) numPallets = 0;

                      const remainingM = length - (numPallets * alongM);
                      const spacingM = Math.max(0, remainingM / (numPallets + 1));
                      const pallets3D = [];

                      const palletBaseColor = defaultPallet.brand === 'loscam' ? '#b91c1c' : defaultPallet.brand === 'chep' ? '#1d4ed8' : '#d97706';

                      for (let pi = 0; pi < numPallets; pi++) {
                        const alongPos = -length / 2 + spacingM + pi * (alongM + spacingM) + alongM / 2;
                        const palletX = isHoriz ? alongPos : 0;
                        const palletZ = isHoriz ? 0 : alongPos;
                        const pDimX = isHoriz ? alongM : crossM;
                        const pDimZ = isHoriz ? crossM : alongM;

                        const emptySlots = area.conveyorConfig?.emptySlots || [];
                        const isEmpty = emptySlots.includes(pi);

                        pallets3D.push(
                          <group key={`conv-pallet-${pi}`} position={[palletX, 0, palletZ]}>
                            <PalletWithCargo3D
                              width={pDimX}
                              length={pDimZ}
                              baseHeight={baseHeightM}
                              cargoHeight={cargoHeightM}
                              baseColor={palletBaseColor}
                              yOffset={convHeight + rollerRadius}
                              isEmpty={isEmpty}
                              emptyStack={1}
                            />
                          </group>
                        );
                      }
                      return pallets3D;
                    })()}

                    {/* 3D Human Operator Simulation beside Conveyor */}
                    {(() => {
                      if (!area.conveyorConfig?.operatorsEnabled) return null;
                      const side = area.conveyorConfig?.operatorSide || 'right';
                      const countPerSide = area.conveyorConfig?.operatorCount || 1;
                      const sides = side === 'both' ? ['left', 'right'] : [side];
                      const spacing = length / (countPerSide + 1);
                      const operators3D = [];

                      sides.forEach(s => {
                        const sideSign = s === 'left' ? -1 : 1;
                        const distFromTrack = width / 2 + 0.45;

                        for (let wi = 0; wi < countPerSide; wi++) {
                          const alongPos = -length / 2 + spacing * (wi + 1);
                          const opX = isHoriz ? alongPos : sideSign * distFromTrack;
                          const opZ = isHoriz ? sideSign * distFromTrack : alongPos;
                          const rotY = isHoriz 
                            ? (s === 'left' ? 0 : Math.PI)
                            : (s === 'left' ? Math.PI / 2 : -Math.PI / 2);

                          operators3D.push(
                            <group key={`op-${s}-${wi}`} position={[opX, 0, opZ]} rotation={[0, rotY, 0]}>
                              <OperatorMesh role={area.conveyorConfig?.operatorRole || 'picking'} showHeightBadge={showDimensions} customModel={activeCustomModel} />
                            </group>
                          );
                        }
                      });

                      return operators3D;
                    })()}

                    {/* Conveyor & Pallet Load Dimension Badges when Show Dimensions is ON */}
                    {showDimensions && (() => {
                      const defaultPallet = mapData.palletTypes?.find(p => p.isPrimary) || mapData.palletTypes?.[0] || mapData.palletSize || {};
                      let totalHeightMm = 1500;
                      if (defaultPallet.totalHeight && defaultPallet.totalHeight > 250) totalHeightMm = defaultPallet.totalHeight;
                      else if (defaultPallet.height && defaultPallet.height > 250) totalHeightMm = defaultPallet.height;
                      else if (mapData.palletSize?.totalHeight && mapData.palletSize.totalHeight > 250) totalHeightMm = mapData.palletSize.totalHeight;
                      else if (mapData.palletSize?.height && mapData.palletSize.height > 250) totalHeightMm = mapData.palletSize.height;
                      
                      const totalHeightM = totalHeightMm / 1000;
                      const badgeY = convHeight + rollerRadius + totalHeightM + 0.35;

                      return (
                        <Html position={[0, badgeY, 0]} center>
                          <div style={{
                            background: 'rgba(30, 58, 138, 0.94)',
                            color: 'white',
                            padding: '3px 8px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                            userSelect: 'none',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
                            border: '1px solid rgba(255,255,255,0.25)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <span>Conveyor H: {Math.round(convHeight * 1000)} mm</span>
                            <span style={{ opacity: 0.4 }}>|</span>
                            <span style={{ color: '#34d399' }}>Pallet Load H: {Math.round(totalHeightMm)} mm</span>
                          </div>
                        </Html>
                      );
                    })()}
                 </group>
              );
              return renderInteractiveArea(area, visualContent, x, z, w, h, convHeight + ph + 0.3);
            }

            if (area.type === 'operator_station') {
              const visualContent = (
                <group>
                  {/* Workstation Floor Mat */}
                  <mesh position={[0, 0.01, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                    <planeGeometry args={[w, h]} />
                    <meshStandardMaterial color="#059669" transparent opacity={0.25} />
                  </mesh>
                  {/* 3D Worker standing in center of station */}
                  <group position={[0, 0, 0]}>
                    <OperatorMesh role="operator" showHeightBadge={showDimensions} customModel={activeCustomModel} />
                  </group>
                </group>
              );
              return renderInteractiveArea(area, visualContent, x, z, w, h, 1.85);
            }

          if (area.type === 'model_3d') {
            const rotDeg = area.modelConfig?.rotationDeg ?? 0;
            const rotY = (rotDeg * Math.PI) / 180;
            const catRaw = (area.modelConfig?.category || '').toLowerCase();
            const nameLower = (area.modelConfig?.modelName || area.label || '').toLowerCase();
            const fileLower = (area.modelConfig?.fileUrl || '').toLowerCase();

            let category = 'reach_truck';
            if (catRaw === 'operator' || nameLower.includes('operator') || nameLower.includes('worker')) {
              category = 'operator';
            } else if (catRaw === 'reach_truck' || nameLower.includes('reach') || nameLower.includes('rae') || nameLower.includes('rre') || fileLower.includes('reach')) {
              category = 'reach_truck';
            } else if (catRaw === 'forklift' || nameLower.includes('forklift')) {
              category = 'forklift';
            } else if (catRaw === 'equipment') {
              category = 'equipment';
            } else {
              category = catRaw || 'reach_truck';
            }

            const modelName = area.modelConfig?.modelName || '3D Asset';
            const fileUrl = area.modelConfig?.fileUrl;
            const targetHeight = area.modelConfig?.targetHeight || (category === 'reach_truck' ? 2.30 : category === 'forklift' ? 2.10 : 1.70);

            const visualContent = (
              <group>
                {/* 3D Asset Floor Mat */}
                <mesh position={[0, 0.01, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                  <planeGeometry args={[w, h]} />
                  <meshStandardMaterial color="#8b5cf6" transparent opacity={0.18} />
                </mesh>
                <mesh position={[0, 0.015, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                  <planeGeometry args={[w * 0.96, h * 0.96]} />
                  <meshStandardMaterial color="#6366f1" transparent opacity={0.1} />
                </mesh>

                {/* Rotated 3D Model Instance */}
                <group position={[0, 0, 0]} rotation={[0, rotY, 0]}>
                  {fileUrl ? (
                    <ModelErrorBoundary fallback={
                      category === 'operator' ? (
                        <HumanWorker3D role="operator" showHeightBadge={showDimensions} />
                      ) : category === 'forklift' ? (
                        <ProceduralForklift3D targetHeight={targetHeight} showHeightBadge={showDimensions} modelName={modelName} />
                      ) : (
                        <ProceduralReachTruck3D targetHeight={targetHeight} showHeightBadge={showDimensions} modelName={modelName} />
                      )
                    }>
                      <React.Suspense fallback={
                        category === 'operator' ? (
                          <HumanWorker3D role="operator" showHeightBadge={showDimensions} />
                        ) : category === 'forklift' ? (
                          <ProceduralForklift3D targetHeight={targetHeight} showHeightBadge={showDimensions} modelName={modelName} />
                        ) : (
                          <ProceduralReachTruck3D targetHeight={targetHeight} showHeightBadge={showDimensions} modelName={modelName} />
                        )
                      }>
                        <GLBModelInstance 
                          url={fileUrl} 
                          targetHeight={targetHeight} 
                          role={modelName} 
                          showHeightBadge={showDimensions} 
                        />
                      </React.Suspense>
                    </ModelErrorBoundary>
                  ) : category === 'operator' ? (
                    <HumanWorker3D role="operator" showHeightBadge={showDimensions} />
                  ) : category === 'forklift' ? (
                    <ProceduralForklift3D targetHeight={targetHeight} showHeightBadge={showDimensions} modelName={modelName} />
                  ) : (
                    <ProceduralReachTruck3D targetHeight={targetHeight} showHeightBadge={showDimensions} modelName={modelName} />
                  )}
                </group>
              </group>
            );
            return renderInteractiveArea(area, visualContent, x, z, w, h, targetHeight || 2.4);
          }
          
          const visualContent = (
             <group>
               <mesh position={[0, 0.01, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                 <planeGeometry args={[w, h]} />
                 <meshBasicMaterial color={
                   area.type === 'pickup' ? '#10b981' : 
                   area.type === 'dropoff' ? '#ef4444' : 
                   '#4b5563'
                 } transparent opacity={0.3} />
               </mesh>
               {((area.type === 'pickup' || area.type === 'dropoff') && area.stationConfig?.showPallet) && (
                 <PalletWithCargo3D
                   width={pw * 0.98}
                   length={pl * 0.98}
                   baseHeight={0.14}
                   cargoHeight={Math.max(0.1, ph - 0.14)}
                   baseColor={palletBaseColor}
                   yOffset={0}
                   isEmpty={area.stationConfig?.palletStatus === 'empty'}
                   emptyStack={area.stationConfig?.emptyPalletStackCount || 1}
                 />
               )}
             </group>
          );
          return renderInteractiveArea(area, visualContent, x, z, w, h, getAreaHeightM(area, ph));
        })}

        {/* Corridors */}
        {mapData.corridors && mapData.corridors.map(cor => {
          const { x, z, w, h } = mapTo3D(cor.x, cor.y, cor.w, cor.h);
          const isSelected = selectedCorridor?.id === cor.id || selectedCorridorIds?.includes(cor.id);
          const isHovered = hoveredCorridorId === cor.id;
          return (
            <group
              key={cor.id}
              position={[x, 0, z]}
              onClick={(e) => handleCorridorClick(cor, e)}
              onPointerDown={(e) => handleStartDragCorridor(cor, e)}
              onPointerMove={(e) => {
                if (dragStateRef.current) {
                  e.stopPropagation();
                  handleFloorPointerMove(e);
                }
              }}
              onPointerOver={(e) => {
                e.stopPropagation();
                setHoveredCorridorId(cor.id);
                if (!dragStateRef.current) {
                  document.body.style.cursor = isPositionLocked ? 'pointer' : 'grab';
                }
              }}
              onPointerOut={(e) => {
                if (hoveredCorridorId === cor.id) {
                  setHoveredCorridorId(null);
                  if (!dragStateRef.current) document.body.style.cursor = 'default';
                }
              }}
            >
              <mesh
                position={[0, 0.05, 0]}
                rotation={[-Math.PI / 2, 0, 0]}
                onClick={(e) => handleCorridorClick(cor, e)}
                onPointerDown={(e) => handleStartDragCorridor(cor, e)}
              >
                <planeGeometry args={[w, h]} />
                <meshBasicMaterial
                  color={isSelected ? "#3b82f6" : "#f59e0b"}
                  transparent
                  opacity={isSelected ? 0.6 : isHovered ? 0.45 : 0.3}
                />
              </mesh>
              {isSelected && (
                <lineSegments position={[0, 0.055, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                  <edgesGeometry args={[new THREE.PlaneGeometry(w, h)]} />
                  <lineBasicMaterial color="#1d4ed8" linewidth={2} />
                </lineSegments>
              )}
            </group>
          );
        })}

        <OrbitControls ref={orbitControlsRef} makeDefault zoomSpeed={0.5} maxPolarAngle={Math.PI / 2 - 0.02} />
      </Canvas>
    </div>
  );
};

export default Map3DViewer;


